import { useState, useEffect, useMemo, useRef, useCallback } from "react";

// ═══════════════════════════════════════════════════════════════
// AGENCY HUB — Fully Integrated PPC & Shopping Feed Platform
// Auth + PPC Pulse + FeedForge + Activity Feed + API Wiring
// ═══════════════════════════════════════════════════════════════

const API_BASE = typeof window !== "undefined" && window.__API_BASE ? window.__API_BASE : "http://localhost:3001";

// ── Theme ──
const C = {
  bg: "#060a11", surface: "#0f1520", surfaceAlt: "#141c2b", card: "#111827",
  border: "#1c2537", borderLight: "#253045",
  text: "#e4e9f1", textSec: "#8694b2", textDim: "#4a5a78",
  accent: "#6366f1", accentLight: "#818cf8", accentDim: "#4f46e5",
  green: "#10b981", greenDim: "#059669", red: "#ef4444", redDim: "#dc2626",
  amber: "#f59e0b", blue: "#3b82f6", purple: "#8b5cf6", cyan: "#06b6d4",
  pink: "#ec4899",
};

const font = "'DM Sans', 'Satoshi', -apple-system, sans-serif";
const mono = "'JetBrains Mono', 'SF Mono', monospace";

// ═══════════════════════════════════════════
// API CLIENT — connects frontend to backend
// Falls back to demo data when no backend
// ═══════════════════════════════════════════
class ApiClient {
  constructor() {
    this.token = null;
    this.isConnected = false;
  }

  setToken(t) { this.token = t; }

  async request(path, opts = {}) {
    const headers = { "Content-Type": "application/json" };
    if (this.token) headers["Authorization"] = `Bearer ${this.token}`;
    try {
      const res = await fetch(`${API_BASE}${path}`, { ...opts, headers, credentials: "include" });
      if (res.status === 401) { this.token = null; throw new Error("Unauthorized"); }
      if (!res.ok) throw new Error(`API ${res.status}`);
      this.isConnected = true;
      return await res.json();
    } catch (err) {
      if (err.message === "Unauthorized") throw err;
      this.isConnected = false;
      return null; // Signal to use demo data
    }
  }

  // Auth
  async login(email, password) { return this.request("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }); }
  async register(name, email, password, inviteToken) { return this.request("/auth/register", { method: "POST", body: JSON.stringify({ name, email, password, inviteToken }) }); }
  async getMe() { return this.request("/auth/me"); }

  // PPC
  async getAccounts() { return this.request("/api/accounts"); }
  async getCampaigns(id) { return this.request(`/api/accounts/${id}/campaigns`); }
  async getImprovements(id) { return this.request(`/api/accounts/${id}/improvements`); }
  async applyImprovement(acctId, impId, action) { return this.request(`/api/accounts/${acctId}/improvements/${impId}/apply`, { method: "POST", body: JSON.stringify({ action }) }); }
  async dismissImprovement(acctId, impId) { return this.request(`/api/accounts/${acctId}/improvements/${impId}/dismiss`, { method: "POST" }); }
  async getErrors(id) { return this.request(`/api/accounts/${id}/errors`); }
  async getSegments(id, dim) { return this.request(`/api/accounts/${id}/segments/${dim}`); }
  async getBudgets(id) { return this.request(`/api/accounts/${id}/budgets`); }
  async getSearchTerms(id) { return this.request(`/api/accounts/${id}/search-terms`); }

  // FeedForge
  async getProducts(params) { const q = params ? "?" + new URLSearchParams(params) : ""; return this.request(`/api/feed/products${q}`); }
  async updateProduct(id, data) { return this.request(`/api/feed/products/${id}`, { method: "PATCH", body: JSON.stringify(data) }); }
  async getRules() { return this.request("/api/feed/rules"); }
  async executeRules() { return this.request("/api/feed/rules/execute", { method: "POST" }); }
  async exportXml() { return this.request("/api/feed/export/google.xml"); }
  async feedAudit() { return this.request("/api/feed/audit"); }
  async syncWoo() { return this.request("/api/feed/sync/woocommerce", { method: "POST" }); }

  // Activity
  async getActivity(params) { const q = params ? "?" + new URLSearchParams(params) : ""; return this.request(`/api/activity${q}`); }
  async getActivitySummary() { return this.request("/api/activity/summary"); }

  // Campaign Builder — push AI plan to Google Ads (created as PAUSED)
  async pushCampaignToAds(customerId, campaignPlan) { return this.request(`/api/accounts/${customerId}/campaigns/build`, { method: "POST", body: JSON.stringify({ campaignPlan }) }); }
}

const api = new ApiClient();

// ═══════════════════════════════════════════
// DEMO DATA — used when no backend connected
// ═══════════════════════════════════════════
const DEMO_ACCOUNTS = [
  { id: "1", name: "Apex Fitness Co.", spend: 12450, budget: 15000, conversions: 342, ctr: 4.2, cpc: 1.87, roas: 5.2, trend: "up", health: 92, impressions: 89200, clicks: 3746, qualityScore: 8.1 },
  { id: "2", name: "Bloom Botanicals", spend: 8320, budget: 10000, conversions: 198, ctr: 3.1, cpc: 2.14, roas: 3.8, trend: "down", health: 71, impressions: 54100, clicks: 1677, qualityScore: 6.4 },
  { id: "3", name: "ClearView Optics", spend: 24100, budget: 28000, conversions: 567, ctr: 5.6, cpc: 1.42, roas: 7.1, trend: "up", health: 96, impressions: 145000, clicks: 8120, qualityScore: 8.9 },
  { id: "4", name: "DriftWood Homes", spend: 15800, budget: 16000, conversions: 89, ctr: 2.8, cpc: 4.56, roas: 2.1, trend: "down", health: 58, impressions: 72300, clicks: 2024, qualityScore: 5.2 },
  { id: "5", name: "EchoStream Audio", spend: 6200, budget: 8000, conversions: 156, ctr: 3.9, cpc: 1.65, roas: 4.4, trend: "up", health: 84, impressions: 38900, clicks: 1517, qualityScore: 7.3 },
  { id: "6", name: "Forge & Anvil", spend: 19500, budget: 22000, conversions: 423, ctr: 4.8, cpc: 1.93, roas: 6.3, trend: "up", health: 89, impressions: 112500, clicks: 5400, qualityScore: 7.8 },
];

const DEMO_IMPROVEMENTS = [
  { id: 1, accountId: "1", type: "keyword", priority: "high", title: "Add negative keyword: 'free workout plans'", description: "This search term has spent £42 with 0 conversions in the last 30 days. Adding as negative will save estimated £140/quarter.", impact: "+£140/qtr", effort: "1-click" },
  { id: 2, accountId: "1", type: "bid", priority: "high", title: "Increase bid on 'personal trainer near me'", description: "This keyword converts at 8.2% but impression share is only 34%. Increasing bid by 15% could capture 120+ more impressions/week.", impact: "+28 conv/mo", effort: "1-click" },
  { id: 3, accountId: "2", type: "ad", priority: "medium", title: "Pause underperforming RSA variant", description: "Ad variant #3 has 0.8% CTR vs account average of 3.1%. Running 45 days with sufficient data.", impact: "+0.4% CTR", effort: "1-click" },
  { id: 4, accountId: "2", type: "budget", priority: "high", title: "Reallocate budget from Display to Search", description: "Display campaigns spending £1,200/mo with ROAS of 0.8x. Search campaigns limited by budget with 6.2x ROAS.", impact: "+£2,400/mo", effort: "Review" },
  { id: 5, accountId: "3", type: "keyword", priority: "low", title: "Add broad match variant: 'eye exam booking'", description: "Search term report shows this query appearing 340 times with 12 conversions but no dedicated keyword.", impact: "+12 conv/mo", effort: "1-click" },
  { id: 6, accountId: "4", type: "ad", priority: "high", title: "Test new headline: mention '3D virtual tours'", description: "Competitor analysis shows top performers highlighting virtual tours. Current ads miss this USP.", impact: "+1.2% CTR est", effort: "5 min" },
  { id: 7, accountId: "4", type: "budget", priority: "critical", title: "Campaign 'Brand Terms' exhausting budget by 2pm", description: "Brand campaign hits daily budget limit by 14:00 every day, missing peak evening traffic.", impact: "+35 conv/mo", effort: "1-click" },
  { id: 8, accountId: "5", type: "keyword", priority: "medium", title: "Negative keyword: 'bluetooth speaker repair'", description: "Repair-related queries driving 8% of clicks but 0% of conversions. £89 wasted in last 30 days.", impact: "+£89/mo saved", effort: "1-click" },
  { id: 9, accountId: "6", type: "bid", priority: "low", title: "Adjust device bids: increase mobile by 12%", description: "Mobile converts 22% better than desktop but has lower avg position.", impact: "+18 conv/mo", effort: "1-click" },
  { id: 10, accountId: "1", type: "shopping", priority: "medium", title: "Update product titles with brand + category", description: "Products with brand in title see 2.3x higher CTR in shopping results.", impact: "+1.8% CTR", effort: "15 min" },
];

const DEMO_ALERTS = [
  { id: 1, accountId: "4", type: "error", message: "DriftWood Homes: Conversion tracking tag not firing since Feb 23", time: "2 hours ago" },
  { id: 2, accountId: "2", type: "warning", message: "Bloom Botanicals: CPA increased 34% week-over-week", time: "5 hours ago" },
  { id: 3, accountId: "1", type: "success", message: "Apex Fitness: New monthly conversion record — 342 conversions", time: "1 day ago" },
  { id: 4, accountId: "3", type: "info", message: "ClearView Optics: Impression share increased to 78%", time: "1 day ago" },
  { id: 5, accountId: "6", type: "warning", message: "Forge & Anvil: Quality Score dropped on 3 keywords", time: "2 days ago" },
];

const DEMO_PERF = Array.from({ length: 30 }, (_, i) => ({
  day: i + 1,
  date: new Date(2026, 0, 27 + i).toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
  spend: 2800 + Math.sin(i * 0.3) * 800 + Math.random() * 400,
  conversions: 55 + Math.sin(i * 0.25) * 20 + Math.random() * 10,
  ctr: 3.5 + Math.sin(i * 0.2) * 1.2 + Math.random() * 0.5,
  cpc: 1.8 + Math.cos(i * 0.15) * 0.4 + Math.random() * 0.2,
}));

const DEMO_SEGMENTS = {
  device: [
    { label: "Mobile", spend: 38200, conversions: 890, ctr: 4.6, cpc: 1.72, share: 44 },
    { label: "Desktop", spend: 32100, conversions: 680, ctr: 3.8, cpc: 2.01, share: 37 },
    { label: "Tablet", spend: 16070, conversions: 205, ctr: 2.9, cpc: 2.34, share: 19 },
  ],
  location: [
    { label: "London", spend: 22400, conversions: 520, ctr: 4.8, cpc: 1.85, share: 26 },
    { label: "Manchester", spend: 14200, conversions: 310, ctr: 4.1, cpc: 1.92, share: 16 },
    { label: "Birmingham", spend: 11800, conversions: 245, ctr: 3.7, cpc: 2.05, share: 14 },
    { label: "Edinburgh", spend: 9600, conversions: 198, ctr: 3.9, cpc: 1.78, share: 11 },
    { label: "Leeds", spend: 8300, conversions: 165, ctr: 3.4, cpc: 2.12, share: 10 },
    { label: "Other", spend: 30070, conversions: 337, ctr: 3.2, cpc: 2.25, share: 23 },
  ],
  audience: [
    { label: "In-market: Fitness", spend: 8400, conversions: 245, ctr: 5.1, roas: 6.8, share: 18 },
    { label: "In-market: Home Decor", spend: 7200, conversions: 89, ctr: 3.2, roas: 2.4, share: 15 },
    { label: "Affinity: Health", spend: 6100, conversions: 178, ctr: 4.4, roas: 5.2, share: 13 },
    { label: "Remarketing: 30d", spend: 5800, conversions: 312, ctr: 7.2, roas: 9.1, share: 12 },
    { label: "Similar Audiences", spend: 4900, conversions: 98, ctr: 3.1, roas: 3.4, share: 10 },
  ],
};

const DEMO_ERRORS = [
  { id: 1, accountId: "4", category: "tracking", severity: "critical", title: "Conversion tracking not firing", description: "Google Tag Manager container on driftwoodhomes.co.uk has not recorded a conversion event since Feb 23. 4 days of data missing.", recommendation: "Check GTM container snippet and verify conversion linker tag is active." },
  { id: 2, accountId: "2", category: "ads", severity: "high", title: "3 ads disapproved", description: "Ads in campaign 'Spring Collection' disapproved for 'Misleading content' policy. Claims '50% off everything' but landing page shows selective discounts.", recommendation: "Update ad copy to reflect actual discount scope." },
  { id: 3, accountId: "4", category: "quality", severity: "high", title: "Quality Score below 4 on 8 keywords", description: "Keywords in 'New Builds' ad group have QS 2-3. Expected CTR: Below average. Ad relevance: Below average.", recommendation: "Create tighter ad groups with dedicated ad copy per keyword theme." },
  { id: 4, accountId: "1", category: "budget", severity: "medium", title: "Search Impression Share lost to budget: 22%", description: "Apex Fitness 'Brand Terms' campaign losing 22% impression share due to budget. Estimated missed clicks: 340/week.", recommendation: "Increase daily budget from £45 to £58 to capture remaining share." },
  { id: 5, accountId: "5", category: "landing", severity: "medium", title: "Slow landing page: 4.8s load time", description: "echostream.co.uk/shop loads in 4.8s on mobile. Google benchmark is <2.5s. May be affecting Quality Score and bounce rate.", recommendation: "Optimize images, enable lazy loading, and consider CDN." },
  { id: 6, accountId: "6", category: "tracking", severity: "low", title: "Enhanced conversions not configured", description: "Enhanced conversions could improve conversion measurement accuracy by 5-15%. Currently using basic tag only.", recommendation: "Enable enhanced conversions in Google Ads and update tag with hashed email." },
];

const DEMO_BUDGETS = [
  { id: 1, accountId: "1", campaign: "Brand Terms", daily: 45, spent: 44.2, pacing: 98, status: "on-track" },
  { id: 2, accountId: "1", campaign: "Generic Fitness", daily: 120, spent: 98.5, pacing: 82, status: "underspend" },
  { id: 3, accountId: "2", campaign: "Spring Collection", daily: 85, spent: 91.2, pacing: 107, status: "overspend" },
  { id: 4, accountId: "3", campaign: "Eye Tests", daily: 200, spent: 195.8, pacing: 98, status: "on-track" },
  { id: 5, accountId: "4", campaign: "New Builds", daily: 150, spent: 150, pacing: 100, status: "limited" },
  { id: 6, accountId: "5", campaign: "Wireless Speakers", daily: 65, spent: 52.3, pacing: 80, status: "underspend" },
  { id: 7, accountId: "6", campaign: "Bespoke Furniture", daily: 180, spent: 178.4, pacing: 99, status: "on-track" },
];

const DEMO_SCORECARD = [
  { category: "Account Structure", criteria: "Ad group keyword count", score: 85, detail: "Average 12 keywords per ad group — good" },
  { category: "Account Structure", criteria: "Single keyword ad groups", score: 70, detail: "34% of ad groups are SKAGs — consider expanding" },
  { category: "Ad Quality", criteria: "RSA ad strength", score: 78, detail: "72% of RSAs rated 'Good' or 'Excellent'" },
  { category: "Ad Quality", criteria: "Ad extension coverage", score: 92, detail: "Sitelinks, callouts, and structured snippets on all campaigns" },
  { category: "Bidding & Budget", criteria: "Smart bidding adoption", score: 88, detail: "5 of 6 accounts using Target CPA or Target ROAS" },
  { category: "Bidding & Budget", criteria: "Budget utilisation", score: 74, detail: "2 campaigns consistently underspending by 15%+" },
  { category: "Keyword Health", criteria: "Search term relevance", score: 81, detail: "91% of search terms relate to target keywords" },
  { category: "Keyword Health", criteria: "Negative keyword coverage", score: 65, detail: "3 accounts missing shared negative keyword lists" },
  { category: "Tracking", criteria: "Conversion tracking", score: 70, detail: "1 account has tracking issues — DriftWood Homes" },
  { category: "Tracking", criteria: "Attribution model", score: 85, detail: "5 of 6 accounts using data-driven attribution" },
];

const DEMO_PRODUCTS = [
  { id: 1, title: "Nike Air Max 270 — Black/White", sku: "NK-AM270-BW", price: 129.99, salePrice: 99.99, category: "Shoes > Running", brand: "Nike", gtin: "0194501234567", stock: 45, status: "active", clicks: 340, conversions: 28, revenue: 2799.72, roas: 5.6, customLabels: ["Sale", "High Performer"] },
  { id: 2, title: "Adidas Ultraboost 22", sku: "AD-UB22-GY", price: 159.99, salePrice: null, category: "Shoes > Running", brand: "Adidas", gtin: "4065427567890", stock: 22, status: "active", clicks: 210, conversions: 15, revenue: 2399.85, roas: 4.2, customLabels: ["Core Range"] },
  { id: 3, title: "New Balance 574 Classic", sku: "NB-574-NV", price: 89.99, salePrice: 74.99, category: "Shoes > Casual", brand: "New Balance", gtin: "0739655987654", stock: 0, status: "out_of_stock", clicks: 89, conversions: 0, revenue: 0, roas: 0, customLabels: ["Budget Waster"] },
  { id: 4, title: "Under Armour HOVR Phantom 3", sku: "UA-HP3-BK", price: 139.99, salePrice: null, category: "Shoes > Running", brand: "Under Armour", gtin: "", stock: 67, status: "active", clicks: 156, conversions: 12, revenue: 1679.88, roas: 3.8, customLabels: ["Missing GTIN"] },
  { id: 5, title: "Puma RS-X Reinvention", sku: "PM-RSX-WH", price: 109.99, salePrice: 89.99, category: "Shoes > Lifestyle", brand: "Puma", gtin: "4060981234567", stock: 8, status: "active", clicks: 78, conversions: 6, revenue: 539.94, roas: 2.1, customLabels: ["Low Stock"] },
];

const DEMO_FEED_RULES = [
  { id: 1, name: "Prepend brand to title", enabled: true, conditions: [{ field: "brand", op: "isNotEmpty" }], actions: [{ type: "prepend", field: "title", value: "{brand} — " }], lastRun: "2 hours ago", affected: 128 },
  { id: 2, name: "Exclude out of stock", enabled: true, conditions: [{ field: "stock", op: "<", value: "1" }], actions: [{ type: "exclude" }], lastRun: "2 hours ago", affected: 12 },
  { id: 3, name: "Label high performers", enabled: true, conditions: [{ field: "roas", op: ">", value: "4" }], actions: [{ type: "set", field: "custom_label_0", value: "High Performer" }], lastRun: "2 hours ago", affected: 34 },
  { id: 4, name: "Fix missing categories", enabled: false, conditions: [{ field: "category", op: "isEmpty" }], actions: [{ type: "set", field: "category", value: "General > Uncategorised" }], lastRun: "3 days ago", affected: 5 },
];

const DEMO_ACTIVITY = [
  { id: 1, module: "ppc", action: "improvement_applied", user: "Sarah M.", summary: "Applied negative keyword 'free workout plans' to Apex Fitness", impact: "£140/qtr saved", time: "12 minutes ago" },
  { id: 2, module: "feedforge", action: "rule_executed", user: "System", summary: "Rule 'Prepend brand to title' ran — 128 products updated", impact: "128 products", time: "2 hours ago" },
  { id: 3, module: "ppc", action: "bid_changed", user: "James K.", summary: "Increased bid on 'personal trainer near me' by 15% for Apex Fitness", impact: "+28 conv/mo est", time: "3 hours ago" },
  { id: 4, module: "feedforge", action: "products_excluded", user: "System", summary: "12 out-of-stock products excluded from feed", impact: "12 products", time: "2 hours ago" },
  { id: 5, module: "ppc", action: "ad_paused", user: "Sarah M.", summary: "Paused underperforming RSA in Bloom Botanicals 'Spring Collection'", impact: "+0.4% CTR", time: "5 hours ago" },
  { id: 6, module: "ppc", action: "budget_changed", user: "James K.", summary: "Increased daily budget for ClearView Optics 'Eye Tests' from £180 to £200", impact: "+12 conv/mo", time: "1 day ago" },
  { id: 7, module: "feedforge", action: "feed_exported", user: "Sarah M.", summary: "Google Shopping XML feed exported — 243 active products", impact: "243 products", time: "1 day ago" },
  { id: 8, module: "ppc", action: "negative_keyword_added", user: "James K.", summary: "Added 'bluetooth speaker repair' as negative for EchoStream Audio", impact: "£89/mo saved", time: "2 days ago" },
];

const DEMO_SEARCH_TERMS = [
  { term: "personal trainer near me", clicks: 245, impressions: 4200, ctr: 5.8, cpc: 1.92, conversions: 28, cost: 470.40, convRate: 11.4, status: "added" },
  { term: "free workout plans", clicks: 89, impressions: 3100, ctr: 2.9, cpc: 1.45, conversions: 0, cost: 129.05, convRate: 0, status: "wasted" },
  { term: "gym membership deals", clicks: 156, impressions: 2800, ctr: 5.6, cpc: 2.10, conversions: 18, cost: 327.60, convRate: 11.5, status: "added" },
  { term: "best running shoes 2026", clicks: 312, impressions: 5400, ctr: 5.8, cpc: 1.38, conversions: 42, cost: 430.56, convRate: 13.5, status: "added" },
  { term: "cheap gym equipment", clicks: 67, impressions: 1900, ctr: 3.5, cpc: 1.82, conversions: 1, cost: 121.94, convRate: 1.5, status: "review" },
  { term: "yoga mat amazon", clicks: 43, impressions: 1200, ctr: 3.6, cpc: 1.55, conversions: 0, cost: 66.65, convRate: 0, status: "wasted" },
  { term: "home workout equipment", clicks: 198, impressions: 3600, ctr: 5.5, cpc: 1.78, conversions: 22, cost: 352.44, convRate: 11.1, status: "added" },
  { term: "personal trainer cost uk", clicks: 134, impressions: 2200, ctr: 6.1, cpc: 2.05, conversions: 15, cost: 274.70, convRate: 11.2, status: "added" },
  { term: "fitness app free", clicks: 52, impressions: 1800, ctr: 2.9, cpc: 1.30, conversions: 0, cost: 67.60, convRate: 0, status: "wasted" },
  { term: "resistance bands set", clicks: 87, impressions: 1500, ctr: 5.8, cpc: 1.22, conversions: 9, cost: 106.14, convRate: 10.3, status: "review" },
  { term: "crossfit gym near me", clicks: 165, impressions: 2900, ctr: 5.7, cpc: 2.35, conversions: 19, cost: 387.75, convRate: 11.5, status: "added" },
  { term: "how to lose weight fast", clicks: 78, impressions: 4500, ctr: 1.7, cpc: 0.95, conversions: 0, cost: 74.10, convRate: 0, status: "wasted" },
];

// ═══════════════════════════════════════════
// SHARED COMPONENTS
// ═══════════════════════════════════════════

const s = {
  page: { background: C.bg, minHeight: "100vh", fontFamily: font, color: C.text },
  card: { background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, padding: 20 },
  cardSm: { background: C.card, borderRadius: 10, border: `1px solid ${C.border}`, padding: 14 },
  input: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", color: C.text, fontSize: 13, fontFamily: font, outline: "none", width: "100%" },
  btn: { background: C.accent, color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: font },
  btnGhost: { background: "transparent", color: C.textSec, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: font },
  badge: (color) => ({ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: `${color}18`, color, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }),
  sidebarItem: (active) => ({ padding: "10px 16px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: active ? 600 : 400, color: active ? C.text : C.textSec, background: active ? `${C.accent}15` : "transparent", borderLeft: active ? `3px solid ${C.accent}` : "3px solid transparent", transition: "all 0.15s", display: "flex", alignItems: "center", gap: 10 }),
};

function KpiCard({ label, value, sub, color, trend }) {
  return (
    <div style={{ ...s.cardSm, flex: 1, minWidth: 140 }}>
      <div style={{ fontSize: 11, color: C.textDim, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || C.text, fontFamily: mono }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: trend === "up" ? C.green : trend === "down" ? C.red : C.textDim, marginTop: 2 }}>{trend === "up" ? "▲" : trend === "down" ? "▼" : ""} {sub}</div>}
    </div>
  );
}

function MiniBar({ value, max, color, height = 6 }) {
  const pct = Math.min((value / max) * 100, 100);
  return (
    <div style={{ background: `${color}15`, borderRadius: height / 2, height, width: "100%" }}>
      <div style={{ background: color, borderRadius: height / 2, height, width: `${pct}%`, transition: "width 0.5s" }} />
    </div>
  );
}

function HealthRing({ score, size = 48, strokeWidth = 4 }) {
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - score / 100);
  const color = score >= 80 ? C.green : score >= 60 ? C.amber : C.red;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={`${color}20`} strokeWidth={strokeWidth} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth} strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.8s" }} />
      <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="central" fill={color} fontSize={size * 0.28} fontWeight="700" fontFamily={mono} style={{ transform: "rotate(90deg)", transformOrigin: "center" }}>{score}</text>
    </svg>
  );
}

function Sparkline({ data, width = 80, height = 24, color = C.accent }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * width},${height - ((v - min) / range) * height}`).join(" ");
  return <svg width={width} height={height}><polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" /></svg>;
}

function PriorityBadge({ priority }) {
  const colors = { critical: C.red, high: "#f97316", medium: C.amber, low: C.textDim };
  return <span style={s.badge(colors[priority] || C.textDim)}>{priority}</span>;
}

function ConnectionBanner({ connected }) {
  if (connected) return null;
  return (
    <div style={{ background: `${C.amber}15`, border: `1px solid ${C.amber}30`, borderRadius: 8, padding: "8px 14px", marginBottom: 16, display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.amber }}>
      <span>⚡</span>
      <span>Demo mode — showing sample data. Connect your backend at <span style={{ fontFamily: mono }}>{API_BASE}</span> to see live data.</span>
    </div>
  );
}

// ═══════════════════════════════════════════
// AUTH GATE
// ═══════════════════════════════════════════
function AuthGate({ children, onAuth }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("admin@agency.com");
  const [password, setPassword] = useState("changeme123");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [skipAuth, setSkipAuth] = useState(false);

  const handleLogin = async () => {
    setLoading(true); setError("");
    try {
      const res = await api.login(email, password);
      if (res && res.token) {
        api.setToken(res.token);
        onAuth(res.user || { name: email.split("@")[0], email, role: "admin" });
      } else {
        // Backend not available — enter demo mode
        setSkipAuth(true);
        onAuth({ name: "Demo User", email: "demo@agency.com", role: "admin" });
      }
    } catch {
      setError("Invalid credentials. Try admin@agency.com / changeme123");
    }
    setLoading(false);
  };

  if (skipAuth) return children;

  return (
    <div style={{ ...s.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 380, ...s.card, padding: 32 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 28, fontWeight: 800, background: `linear-gradient(135deg, ${C.accent}, ${C.cyan})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", marginBottom: 4 }}>Agency Hub</div>
          <div style={{ fontSize: 13, color: C.textDim }}>PPC Pulse + FeedForge</div>
        </div>

        {error && <div style={{ background: `${C.red}15`, border: `1px solid ${C.red}30`, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: C.red, marginBottom: 12 }}>{error}</div>}

        {mode === "login" ? (
          <>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: C.textDim, marginBottom: 4, display: "block" }}>Email</label>
              <input style={s.input} value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@agency.com" />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: C.textDim, marginBottom: 4, display: "block" }}>Password</label>
              <input style={s.input} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" onKeyDown={e => e.key === "Enter" && handleLogin()} />
            </div>
            <button style={{ ...s.btn, width: "100%", padding: "10px 16px", opacity: loading ? 0.7 : 1 }} onClick={handleLogin} disabled={loading}>{loading ? "Signing in..." : "Sign In"}</button>
            <div style={{ textAlign: "center", marginTop: 12, fontSize: 12, color: C.textDim }}>
              <span onClick={() => setMode("register")} style={{ color: C.accent, cursor: "pointer" }}>Create account</span>
              <span style={{ margin: "0 8px" }}>·</span>
              <span onClick={() => { setSkipAuth(true); onAuth({ name: "Demo User", email: "demo@agency.com", role: "admin" }); }} style={{ color: C.textDim, cursor: "pointer" }}>Enter demo mode →</span>
            </div>
            <div style={{ marginTop: 16, background: C.surface, borderRadius: 8, padding: "8px 12px", fontSize: 11, color: C.textDim }}>
              Default: admin@agency.com / changeme123
            </div>
            <div style={{ marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
              <div style={{ fontSize: 10, color: C.textDim, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, textAlign: "center" }}>Demo as different role</div>
              <div style={{ display: "flex", gap: 6 }}>
                {[
                  { role: "admin", label: "Agency Owner", color: "#ef4444", email: "admin@agency.com", name: "James K." },
                  { role: "manager", label: "Acc. Manager", color: "#6366f1", email: "sarah@agency.com", name: "Sarah M." },
                  { role: "viewer", label: "Client", color: "#10b981", email: "client@apexfitness.com", name: "Tom (Apex Fitness)", accountName: "Apex Fitness Co." },
                ].map(r => (
                  <button key={r.role} style={{ flex: 1, background: `${r.color}15`, border: `1px solid ${r.color}30`, borderRadius: 8, padding: "8px 6px", cursor: "pointer", fontFamily: font, textAlign: "center" }}
                    onClick={() => { setSkipAuth(true); onAuth({ name: r.name, email: r.email, role: r.role, accountName: r.accountName }); }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: r.color }}>{r.label}</div>
                    <div style={{ fontSize: 10, color: C.textDim, marginTop: 2 }}>{r.role}</div>
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: C.textDim, marginBottom: 4, display: "block" }}>Name</label>
              <input style={s.input} value={name} onChange={e => setName(e.target.value)} placeholder="Your name" />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: C.textDim, marginBottom: 4, display: "block" }}>Email</label>
              <input style={s.input} value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: C.textDim, marginBottom: 4, display: "block" }}>Password</label>
              <input style={s.input} type="password" value={password} onChange={e => setPassword(e.target.value)} />
            </div>
            <button style={{ ...s.btn, width: "100%", padding: "10px 16px" }} onClick={async () => {
              const res = await api.register(name, email, password);
              if (res?.token) { api.setToken(res.token); onAuth(res.user); }
              else { setSkipAuth(true); onAuth({ name: name || "User", email, role: "manager" }); }
            }}>Create Account</button>
            <div style={{ textAlign: "center", marginTop: 12, fontSize: 12 }}>
              <span onClick={() => setMode("login")} style={{ color: C.accent, cursor: "pointer" }}>Back to login</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// PPC PULSE MODULE
// ═══════════════════════════════════════════
function PPCPulse() {
  const [view, setView] = useState("dashboard");
  const [accounts, setAccounts] = useState(DEMO_ACCOUNTS);
  const [improvements, setImprovements] = useState(DEMO_IMPROVEMENTS);
  const [alerts, setAlerts] = useState(DEMO_ALERTS);
  const [errors, setErrors] = useState(DEMO_ERRORS);
  const [budgets, setBudgets] = useState(DEMO_BUDGETS);
  const [perfData] = useState(DEMO_PERF);
  const [segments] = useState(DEMO_SEGMENTS);
  const [scorecard] = useState(DEMO_SCORECARD);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [dismissed, setDismissed] = useState(new Set());
  const [applied, setApplied] = useState(new Set());
  const [impFilter, setImpFilter] = useState("all");
  const [segDim, setSegDim] = useState("device");
  const [errorFilter, setErrorFilter] = useState("all");
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reportGenerated, setReportGenerated] = useState(false);
  const [stFilter, setStFilter] = useState("all");
  const [stSort, setStSort] = useState("cost");

  // ── Fetch live data from API on mount ──
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const acctRes = await api.getAccounts();
        if (acctRes && acctRes.accounts) {
          setAccounts(acctRes.accounts);
          setConnected(true);
          const allImps = [];
          for (const a of acctRes.accounts.slice(0, 10)) {
            const impRes = await api.getImprovements(a.id);
            if (impRes?.improvements) allImps.push(...impRes.improvements.map(i => ({ ...i, accountId: a.id })));
          }
          if (allImps.length) setImprovements(allImps);
        }
      } catch (e) { console.warn("API not available, using demo data"); }
      setLoading(false);
    })();
  }, []);

  const filteredImps = useMemo(() => {
    let items = improvements.filter(i => !dismissed.has(i.id) && !applied.has(i.id));
    if (selectedAccount) items = items.filter(i => String(i.accountId) === String(selectedAccount));
    if (impFilter !== "all") items = items.filter(i => i.type === impFilter);
    return items.sort((a, b) => ({ critical: 0, high: 1, medium: 2, low: 3 }[a.priority] ?? 4) - ({ critical: 0, high: 1, medium: 2, low: 3 }[b.priority] ?? 4));
  }, [improvements, selectedAccount, dismissed, applied, impFilter]);

  const filteredErrors = useMemo(() => {
    let items = [...errors];
    if (selectedAccount) items = items.filter(e => String(e.accountId) === String(selectedAccount));
    if (errorFilter !== "all") items = items.filter(e => e.category === errorFilter);
    return items.sort((a, b) => ({ critical: 0, high: 1, medium: 2, low: 3 }[a.severity] ?? 4) - ({ critical: 0, high: 1, medium: 2, low: 3 }[b.severity] ?? 4));
  }, [errors, selectedAccount, errorFilter]);

  const totalSpend = accounts.reduce((s, a) => s + (a.spend || 0), 0);
  const totalConversions = accounts.reduce((s, a) => s + (a.conversions || 0), 0);
  const avgCtr = accounts.length ? (accounts.reduce((s, a) => s + (a.ctr || 0), 0) / accounts.length).toFixed(1) : 0;
  const avgRoas = accounts.length ? (accounts.reduce((s, a) => s + (a.roas || 0), 0) / accounts.length).toFixed(1) : 0;
  const avgHealth = accounts.length ? Math.round(accounts.reduce((s, a) => s + (a.health || 75), 0) / accounts.length) : 0;
  const pendingImps = filteredImps.length;

  const handleApply = async (imp) => {
    if (connected) await api.applyImprovement(imp.accountId, imp.id, imp.action || { type: imp.type });
    setApplied(prev => new Set([...prev, imp.id]));
  };

  const handleDismiss = async (imp) => {
    if (connected) await api.dismissImprovement(imp.accountId, imp.id);
    setDismissed(prev => new Set([...prev, imp.id]));
  };

  const NAV = [
    { id: "dashboard", label: "Overview", icon: "◉" },
    { id: "opportunity", label: "Opportunity", icon: "💎" },
    { id: "improvements", label: "Improvements", icon: "⚡", count: pendingImps },
    { id: "performance", label: "Performance", icon: "📊" },
    { id: "segments", label: "Segments", icon: "◧" },
    { id: "budgets", label: "Budgets", icon: "£" },
    { id: "searchTerms", label: "Search Terms", icon: "🔍" },
    { id: "errors", label: "Error Detection", icon: "⚠" },
    { id: "scorecard", label: "Scorecard", icon: "★" },
    { id: "reports", label: "Reports", icon: "📄" },
    { id: "accounts", label: "Accounts", icon: "◇" },
  ];

  // ── Sidebar ──
  const sidebar = (
    <div style={{ width: 200, borderRight: `1px solid ${C.border}`, padding: "16px 8px", display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 }}>
      <div style={{ padding: "0 12px 12px", fontSize: 15, fontWeight: 700, color: C.accent, display: "flex", alignItems: "center", gap: 8 }}>⚡ PPC Pulse</div>
      {NAV.map(n => (
        <div key={n.id} style={s.sidebarItem(view === n.id)} onClick={() => setView(n.id)}>
          <span style={{ width: 18, textAlign: "center", fontSize: 13 }}>{n.icon}</span>
          <span>{n.label}</span>
          {n.count ? <span style={{ marginLeft: "auto", background: C.accent, color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>{n.count}</span> : null}
        </div>
      ))}
      <div style={{ marginTop: "auto", padding: "12px 12px 4px" }}>
        <select style={{ ...s.input, fontSize: 12, padding: "6px 8px" }} value={selectedAccount || ""} onChange={e => setSelectedAccount(e.target.value || null)}>
          <option value="">All accounts</option>
          {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>
    </div>
  );

  // ── Dashboard View ──
  const renderDashboard = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <ConnectionBanner connected={connected} />
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <KpiCard label="Total Spend" value={`£${(totalSpend / 1000).toFixed(1)}k`} sub="+8.2% vs last month" trend="up" color={C.text} />
        <KpiCard label="Conversions" value={totalConversions.toLocaleString()} sub="+12.4%" trend="up" color={C.green} />
        <KpiCard label="Avg CTR" value={`${avgCtr}%`} sub="+0.3pp" trend="up" color={C.blue} />
        <KpiCard label="Avg ROAS" value={`${avgRoas}x`} sub="-0.2x" trend="down" color={C.purple} />
      </div>

      {/* Health Overview */}
      <div style={{ display: "flex", gap: 12 }}>
        <div style={{ ...s.card, flex: 1, display: "flex", alignItems: "center", gap: 16 }}>
          <HealthRing score={avgHealth} size={64} strokeWidth={5} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Account Health</div>
            <div style={{ fontSize: 12, color: C.textDim }}>{accounts.filter(a => (a.health || 75) >= 80).length} of {accounts.length} accounts healthy</div>
          </div>
        </div>
        <div style={{ ...s.card, flex: 1, display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ width: 64, height: 64, borderRadius: 12, background: `${C.accent}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>⚡</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{pendingImps} Improvements</div>
            <div style={{ fontSize: 12, color: C.textDim }}>{filteredImps.filter(i => i.priority === "critical" || i.priority === "high").length} high priority</div>
          </div>
        </div>
        <div style={{ ...s.card, flex: 1, display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ width: 64, height: 64, borderRadius: 12, background: `${C.red}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>⚠</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{filteredErrors.length} Issues</div>
            <div style={{ fontSize: 12, color: C.textDim }}>{filteredErrors.filter(e => e.severity === "critical").length} critical</div>
          </div>
        </div>
      </div>

      {/* Performance Chart */}
      <div style={s.card}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>30-Day Spend & Conversions</div>
        <div style={{ display: "flex", height: 120, alignItems: "flex-end", gap: 2 }}>
          {perfData.map((d, i) => {
            const maxSpend = Math.max(...perfData.map(p => p.spend));
            return (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <div style={{ width: "100%", background: C.accent, borderRadius: 2, height: `${(d.spend / maxSpend) * 100}px`, opacity: 0.7, transition: "height 0.3s" }} title={`£${d.spend.toFixed(0)} — ${d.date}`} />
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 10, color: C.textDim }}>
          <span>{perfData[0]?.date}</span><span>{perfData[14]?.date}</span><span>{perfData[29]?.date}</span>
        </div>
      </div>

      {/* Recent Alerts */}
      <div style={s.card}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Recent Alerts</div>
        {alerts.slice(0, 5).map(a => {
          const col = { error: C.red, warning: C.amber, success: C.green, info: C.blue }[a.type];
          return (
            <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ width: 8, height: 8, borderRadius: 4, background: col, flexShrink: 0 }} />
              <span style={{ fontSize: 13, flex: 1 }}>{a.message}</span>
              <span style={{ fontSize: 11, color: C.textDim, whiteSpace: "nowrap" }}>{a.time}</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── Opportunity Engine — calculated from live data ──
  const renderOpportunity = () => {
    // === Calculate opportunity signals from all data sources ===

    // 1. Wasted spend from search terms
    const wastedTerms = DEMO_SEARCH_TERMS.filter(t => t.status === "wasted");
    const wastedSpendMonthly = wastedTerms.reduce((s, t) => s + t.cost, 0);
    const wastedSpendAnnual = wastedSpendMonthly * 12;

    // 2. Budget headroom — accounts underspending their budgets
    const budgetHeadroom = accounts.reduce((s, a) => s + Math.max(0, (a.budget || 0) - (a.spend || 0)), 0);

    // 3. Impression share gap — estimate from errors/scorecard data
    const isLostToBudget = errors.filter(e => e.category === "budget").length > 0;
    const avgImprShare = 68; // from scorecard data
    const impShareGap = 100 - avgImprShare;
    const estMissedClicks = Math.round(totalSpend / (accounts.length ? accounts.reduce((s, a) => s + (a.cpc || 2), 0) / accounts.length : 2) * (impShareGap / 100));
    const estMissedConv = Math.round(estMissedClicks * (totalConversions / (accounts.reduce((s, a) => s + (a.clicks || 0), 0) || 1)));

    // 4. Quality Score opportunity — keywords below QS 7 have improvement potential
    const lowQSAccounts = accounts.filter(a => (a.qualityScore || 7) < 7);
    const qsSavings = lowQSAccounts.reduce((s, a) => {
      const currentCPC = a.cpc || 2;
      const potentialCPC = currentCPC * 0.8; // QS improvement typically reduces CPC 15-20%
      return s + ((currentCPC - potentialCPC) * (a.clicks || 0));
    }, 0);

    // 5. Conversion rate optimisation — accounts with below-average CR
    const avgCR = accounts.length ? accounts.reduce((s, a) => s + ((a.conversions || 0) / Math.max(a.clicks || 1, 1)), 0) / accounts.length : 0.05;
    const crOppAccounts = accounts.filter(a => ((a.conversions || 0) / Math.max(a.clicks || 1, 1)) < avgCR);
    const crOppConversions = crOppAccounts.reduce((s, a) => {
      const currentCR = (a.conversions || 0) / Math.max(a.clicks || 1, 1);
      const extraConv = Math.round((avgCR - currentCR) * (a.clicks || 0));
      return s + extraConv;
    }, 0);

    // 6. Unapplied improvements — value sitting on the table
    const unappliedImps = improvements.filter(i => !dismissed.has(i.id) && !applied.has(i.id));
    const impactValues = unappliedImps.map(i => {
      const match = (i.impact || "").match(/£([\d,.]+)/);
      return match ? parseFloat(match[1].replace(",", "")) : 0;
    });
    const totalImpactValue = impactValues.reduce((s, v) => s + v, 0);

    // 7. ROAS scaling — high-ROAS accounts that could handle more budget
    const highRoas = accounts.filter(a => (a.roas || 0) >= 5 && (a.spend || 0) < (a.budget || 0));
    const scalableSpend = highRoas.reduce((s, a) => s + ((a.budget || 0) - (a.spend || 0)), 0);
    const estExtraRevenue = scalableSpend * (highRoas.length ? highRoas.reduce((s, a) => s + (a.roas || 0), 0) / highRoas.length : 5);

    // 8. Error-related missed revenue
    const criticalErrors = errors.filter(e => e.severity === "critical");
    const trackingErrors = errors.filter(e => e.category === "tracking");

    // Total opportunity value
    const totalOppValue = wastedSpendAnnual + qsSavings * 12 + estExtraRevenue + totalImpactValue * 12;

    // Per-account opportunity breakdown
    const accountOpps = accounts.map(a => {
      const headroom = Math.max(0, (a.budget || 0) - (a.spend || 0));
      const qsGap = Math.max(0, 8 - (a.qualityScore || 7));
      const cpcSaving = qsGap > 0 ? (a.cpc || 2) * 0.04 * qsGap * (a.clicks || 0) : 0;
      const crGap = Math.max(0, avgCR - ((a.conversions || 0) / Math.max(a.clicks || 1, 1)));
      const extraConv = Math.round(crGap * (a.clicks || 0));
      const hasErrors = errors.filter(e => e.accountId === a.id).length;
      const pendingImps = unappliedImps.filter(i => i.accountId === a.id).length;
      const oppScore = Math.min(100, Math.round(
        (headroom > 500 ? 15 : 0) + (qsGap > 0 ? qsGap * 8 : 0) + (extraConv > 5 ? 20 : 0) +
        (hasErrors ? 15 : 0) + (pendingImps * 5) + ((a.roas || 0) >= 5 ? 15 : 0)
      ));
      return { ...a, headroom, cpcSaving, extraConv, hasErrors, pendingImps, oppScore };
    }).sort((a, b) => b.oppScore - a.oppScore);

    const oppColor = (score) => score >= 60 ? C.green : score >= 30 ? C.amber : C.textDim;

    return (
      <div>
        {/* Hero KPIs */}
        <div style={{ background: `linear-gradient(135deg, ${C.accent}15, ${C.purple}10)`, borderRadius: 14, border: `1px solid ${C.accent}25`, padding: 20, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 20 }}>💎</span>
            <div><div style={{ fontSize: 18, fontWeight: 800 }}>Opportunity Engine</div><div style={{ fontSize: 12, color: C.textDim }}>Calculated from your live account data — potential revenue and savings across the portfolio</div></div>
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 140, background: C.card, borderRadius: 10, padding: 14, border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 10, color: C.textDim, textTransform: "uppercase", letterSpacing: "0.06em" }}>Total Opportunity</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: C.green, fontFamily: mono }}>£{Math.round(totalOppValue).toLocaleString()}</div>
              <div style={{ fontSize: 11, color: C.textDim }}>annualised potential</div>
            </div>
            <KpiCard label="Wasted Spend" value={`£${Math.round(wastedSpendAnnual).toLocaleString()}`} color={C.red} sub={`${wastedTerms.length} terms · £${Math.round(wastedSpendMonthly)}/mo`} />
            <KpiCard label="QS Savings" value={`£${Math.round(qsSavings).toLocaleString()}/mo`} color={C.amber} sub={`${lowQSAccounts.length} accounts below QS 7`} />
            <KpiCard label="Scalable Revenue" value={`£${Math.round(estExtraRevenue).toLocaleString()}`} color={C.blue} sub={`${highRoas.length} accounts with headroom`} />
          </div>
        </div>

        {/* Opportunity Breakdown Cards */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          {/* Wasted Spend */}
          <div style={{ ...s.card, borderLeft: `3px solid ${C.red}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: C.red }}>🗑</span> Wasted Spend Recovery</div>
            <div style={{ fontSize: 12, color: C.textSec, marginBottom: 8 }}>{wastedTerms.length} search terms spending with zero conversions. Adding as negatives would recover:</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.red, fontFamily: mono, marginBottom: 4 }}>£{Math.round(wastedSpendMonthly)}/mo</div>
            {wastedTerms.slice(0, 3).map((t, i) => (
              <div key={i} style={{ fontSize: 11, color: C.textDim, display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                <span>"{t.term}"</span><span style={{ fontFamily: mono, color: C.red }}>£{t.cost.toFixed(0)}</span>
              </div>
            ))}
            <div style={{ fontSize: 11, color: C.accent, marginTop: 6, cursor: "pointer" }} onClick={() => setView("searchTerms")}>→ View all in Search Terms</div>
          </div>

          {/* Impression Share */}
          <div style={{ ...s.card, borderLeft: `3px solid ${C.blue}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: C.blue }}>📡</span> Impression Share Gap</div>
            <div style={{ fontSize: 12, color: C.textSec, marginBottom: 8 }}>You're capturing ~{avgImprShare}% of available impressions. The remaining {impShareGap}% represents:</div>
            <div style={{ display: "flex", gap: 16, marginBottom: 4 }}>
              <div><div style={{ fontSize: 18, fontWeight: 800, color: C.blue, fontFamily: mono }}>{estMissedClicks.toLocaleString()}</div><div style={{ fontSize: 10, color: C.textDim }}>est. missed clicks/mo</div></div>
              <div><div style={{ fontSize: 18, fontWeight: 800, color: C.green, fontFamily: mono }}>{estMissedConv}</div><div style={{ fontSize: 10, color: C.textDim }}>est. missed conv/mo</div></div>
            </div>
            <MiniBar value={avgImprShare} max={100} color={C.blue} height={8} />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: C.textDim, marginTop: 3 }}><span>Current: {avgImprShare}%</span><span>Available: 100%</span></div>
          </div>

          {/* Quality Score */}
          <div style={{ ...s.card, borderLeft: `3px solid ${C.amber}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: C.amber }}>⭐</span> Quality Score Uplift</div>
            <div style={{ fontSize: 12, color: C.textSec, marginBottom: 8 }}>{lowQSAccounts.length} accounts have average QS below 7. Improving to 7+ typically reduces CPC by 15-20%.</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.amber, fontFamily: mono, marginBottom: 4 }}>£{Math.round(qsSavings)}/mo saved</div>
            {lowQSAccounts.map((a, i) => (
              <div key={i} style={{ fontSize: 11, display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                <span style={{ color: C.textSec }}>{a.name}</span>
                <span style={{ fontFamily: mono, color: C.amber }}>QS {a.qualityScore} → 7+ target</span>
              </div>
            ))}
          </div>

          {/* Conversion Rate */}
          <div style={{ ...s.card, borderLeft: `3px solid ${C.purple}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: C.purple }}>📈</span> Conversion Rate Gap</div>
            <div style={{ fontSize: 12, color: C.textSec, marginBottom: 8 }}>{crOppAccounts.length} accounts converting below the portfolio average ({(avgCR * 100).toFixed(1)}%).</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.purple, fontFamily: mono, marginBottom: 4 }}>+{crOppConversions} conv/mo</div>
            <div style={{ fontSize: 11, color: C.textDim }}>If underperforming accounts matched the avg conversion rate, you'd gain an additional {crOppConversions} conversions monthly across {crOppAccounts.length} accounts.</div>
          </div>

          {/* Unapplied Improvements */}
          <div style={{ ...s.card, borderLeft: `3px solid ${C.accent}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: C.accent }}>⚡</span> Pending Improvements</div>
            <div style={{ fontSize: 12, color: C.textSec, marginBottom: 8 }}>{unappliedImps.length} AI-recommended improvements sitting unapplied with estimated combined value:</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.accent, fontFamily: mono, marginBottom: 4 }}>£{Math.round(totalImpactValue)}/mo</div>
            {unappliedImps.filter(i => impactValues[unappliedImps.indexOf(i)] > 0).slice(0, 3).map((i, idx) => (
              <div key={idx} style={{ fontSize: 11, color: C.textDim, display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                <span>{i.title.substring(0, 40)}...</span><span style={{ fontFamily: mono, color: C.accent }}>{i.impact}</span>
              </div>
            ))}
            <div style={{ fontSize: 11, color: C.accent, marginTop: 6, cursor: "pointer" }} onClick={() => setView("improvements")}>→ Apply improvements now</div>
          </div>

          {/* ROAS Scaling */}
          <div style={{ ...s.card, borderLeft: `3px solid ${C.green}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: C.green }}>🚀</span> Scale High Performers</div>
            <div style={{ fontSize: 12, color: C.textSec, marginBottom: 8 }}>{highRoas.length} accounts with ROAS 5x+ have unused budget headroom. Increasing spend at current ROAS:</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.green, fontFamily: mono, marginBottom: 4 }}>£{Math.round(estExtraRevenue).toLocaleString()}</div>
            {highRoas.map((a, i) => (
              <div key={i} style={{ fontSize: 11, display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                <span style={{ color: C.textSec }}>{a.name} ({a.roas}x ROAS)</span>
                <span style={{ fontFamily: mono, color: C.green }}>£{((a.budget || 0) - (a.spend || 0)).toLocaleString()} headroom</span>
              </div>
            ))}
          </div>
        </div>

        {/* Per-Account Opportunity Ranking */}
        <div style={s.card}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Account Opportunity Ranking</div>
          <div style={{ fontSize: 12, color: C.textDim, marginBottom: 12 }}>Ranked by total opportunity score — factoring in budget headroom, QS gap, conversion rate potential, pending improvements, errors, and ROAS scalability.</div>
          {accountOpps.map((a, i) => (
            <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < accountOpps.length - 1 ? `1px solid ${C.border}` : "none" }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: `${oppColor(a.oppScore)}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: oppColor(a.oppScore), fontFamily: mono, border: `2px solid ${oppColor(a.oppScore)}30`, flexShrink: 0 }}>{a.oppScore}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{a.name}</div>
                <div style={{ display: "flex", gap: 12, fontSize: 11, color: C.textDim, marginTop: 2, flexWrap: "wrap" }}>
                  {a.headroom > 0 && <span>£{a.headroom.toLocaleString()} budget headroom</span>}
                  {a.cpcSaving > 0 && <span>£{Math.round(a.cpcSaving)}/mo QS savings</span>}
                  {a.extraConv > 0 && <span>+{a.extraConv} conv from CR uplift</span>}
                  {a.hasErrors > 0 && <span style={{ color: C.red }}>{a.hasErrors} errors to fix</span>}
                  {a.pendingImps > 0 && <span style={{ color: C.accent }}>{a.pendingImps} improvements pending</span>}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 12, fontFamily: mono, color: (a.roas || 0) >= 5 ? C.green : (a.roas || 0) >= 3 ? C.text : C.red }}>{a.roas}x ROAS</div>
                <div style={{ fontSize: 10, color: C.textDim }}>QS {a.qualityScore}</div>
              </div>
              <MiniBar value={a.oppScore} max={100} color={oppColor(a.oppScore)} height={6} />
            </div>
          ))}
        </div>

        {/* Critical Blockers */}
        {(criticalErrors.length > 0 || trackingErrors.length > 0) && (
          <div style={{ ...s.card, marginTop: 12, borderLeft: `3px solid ${C.red}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: C.red }}>⛔ Blockers Reducing Opportunity</div>
            <div style={{ fontSize: 12, color: C.textSec }}>These issues are actively preventing you from capturing available opportunity:</div>
            {criticalErrors.concat(trackingErrors).map((e, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
                <span style={s.badge(C.red)}>{e.severity}</span>
                <span style={{ fontSize: 12 }}>{e.title}</span>
                <span style={{ fontSize: 11, color: C.textDim, marginLeft: "auto" }}>{accounts.find(a => a.id === e.accountId)?.name}</span>
              </div>
            ))}
            <div style={{ fontSize: 11, color: C.accent, marginTop: 8, cursor: "pointer" }} onClick={() => setView("errors")}>→ Fix errors now</div>
          </div>
        )}
      </div>
    );
  };

  // ── Improvements View ──
  const renderImprovements = () => (
    <div>
      <ConnectionBanner connected={connected} />
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {["all", "keyword", "bid", "ad", "budget", "shopping"].map(f => (
          <button key={f} style={{ ...s.btnGhost, ...(impFilter === f ? { background: `${C.accent}20`, color: C.accent, borderColor: `${C.accent}40` } : {}) }} onClick={() => setImpFilter(f)}>
            {f === "all" ? `All (${improvements.filter(i => !dismissed.has(i.id) && !applied.has(i.id)).length})` : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filteredImps.length === 0 && <div style={{ textAlign: "center", padding: 40, color: C.textDim }}>No pending improvements — you're all caught up! ✓</div>}
        {filteredImps.map(imp => {
          const acct = accounts.find(a => String(a.id) === String(imp.accountId));
          return (
            <div key={imp.id} style={{ ...s.card, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <PriorityBadge priority={imp.priority} />
                <span style={s.badge(C.blue)}>{imp.type}</span>
                {acct && <span style={{ fontSize: 11, color: C.textDim }}>{acct.name}</span>}
                <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 12, color: C.green, fontWeight: 600 }}>{imp.impact}</span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{imp.title}</div>
              <div style={{ fontSize: 12, color: C.textSec, lineHeight: 1.5 }}>{imp.description}</div>
              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <button style={{ ...s.btn, padding: "6px 14px", fontSize: 12 }} onClick={() => handleApply(imp)}>✓ Apply</button>
                <button style={{ ...s.btnGhost, padding: "6px 14px", fontSize: 12 }} onClick={() => handleDismiss(imp)}>Dismiss</button>
                <span style={{ marginLeft: "auto", fontSize: 11, color: C.textDim, alignSelf: "center" }}>Effort: {imp.effort}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── Performance View ──
  const renderPerformance = () => (
    <div>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Account Performance Comparison</div>
      <div style={{ ...s.card, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {["Account", "Spend", "Conv.", "CTR", "CPC", "ROAS", "Health"].map(h => (
                <th key={h} style={{ textAlign: "left", padding: "10px 12px", color: C.textDim, fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {accounts.map(a => (
              <tr key={a.id} style={{ borderBottom: `1px solid ${C.border}08` }}>
                <td style={{ padding: "10px 12px", fontWeight: 600 }}>{a.name}</td>
                <td style={{ padding: "10px 12px", fontFamily: mono }}>£{(a.spend || 0).toLocaleString()}</td>
                <td style={{ padding: "10px 12px", fontFamily: mono }}>{a.conversions || 0}</td>
                <td style={{ padding: "10px 12px", fontFamily: mono }}>{a.ctr || 0}%</td>
                <td style={{ padding: "10px 12px", fontFamily: mono }}>£{(a.cpc || 0).toFixed(2)}</td>
                <td style={{ padding: "10px 12px", fontFamily: mono, color: (a.roas || 0) >= 4 ? C.green : (a.roas || 0) >= 2 ? C.amber : C.red }}>{a.roas || 0}x</td>
                <td style={{ padding: "10px 12px" }}><HealthRing score={a.health || 75} size={32} strokeWidth={3} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ── Segments View ──
  const renderSegments = () => {
    const dims = ["device", "location", "audience"];
    const data = segments[segDim] || [];
    const maxVal = Math.max(...data.map(d => d.conversions || d.spend || 0));
    return (
      <div>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {dims.map(d => (
            <button key={d} style={{ ...s.btnGhost, ...(segDim === d ? { background: `${C.accent}20`, color: C.accent, borderColor: `${C.accent}40` } : {}) }} onClick={() => setSegDim(d)}>
              {d.charAt(0).toUpperCase() + d.slice(1)}
            </button>
          ))}
        </div>
        <div style={{ ...s.card, display: "flex", flexDirection: "column", gap: 12 }}>
          {data.map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ width: 120, fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.label}</span>
              <div style={{ flex: 1 }}>
                <MiniBar value={item.conversions || item.spend} max={maxVal} color={[C.accent, C.cyan, C.purple, C.green, C.amber, C.pink][i % 6]} height={8} />
              </div>
              <span style={{ fontFamily: mono, fontSize: 12, width: 60, textAlign: "right" }}>{(item.conversions || item.spend || 0).toLocaleString()}</span>
              <span style={{ fontFamily: mono, fontSize: 11, width: 50, textAlign: "right", color: C.textDim }}>{item.share || 0}%</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── Budgets View ──
  const renderBudgets = () => (
    <div>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Budget Pacing</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {budgets.filter(b => !selectedAccount || String(b.accountId) === String(selectedAccount)).map(b => {
          const acct = accounts.find(a => String(a.id) === String(b.accountId));
          const pacingColor = b.pacing > 105 ? C.red : b.pacing < 85 ? C.amber : C.green;
          return (
            <div key={b.id} style={{ ...s.card, display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{b.campaign}</span>
                  {acct && <span style={{ fontSize: 11, color: C.textDim }}>· {acct.name}</span>}
                </div>
                <MiniBar value={b.spent} max={b.daily} color={pacingColor} height={6} />
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 11, color: C.textDim }}>
                  <span>£{b.spent.toFixed(2)} of £{b.daily}/day</span>
                  <span style={{ color: pacingColor, fontWeight: 600 }}>{b.pacing}% pacing</span>
                </div>
              </div>
              <span style={s.badge(pacingColor)}>{b.status}</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── Errors View ──
  const renderErrors = () => (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {["all", "tracking", "ads", "quality", "budget", "landing"].map(f => (
          <button key={f} style={{ ...s.btnGhost, ...(errorFilter === f ? { background: `${C.red}15`, color: C.red, borderColor: `${C.red}30` } : {}) }} onClick={() => setErrorFilter(f)}>
            {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filteredErrors.map(err => {
          const acct = accounts.find(a => String(a.id) === String(err.accountId));
          return (
            <div key={err.id} style={{ ...s.card, borderLeft: `3px solid ${err.severity === "critical" ? C.red : err.severity === "high" ? "#f97316" : C.amber}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <PriorityBadge priority={err.severity} />
                <span style={s.badge(C.blue)}>{err.category}</span>
                {acct && <span style={{ fontSize: 11, color: C.textDim }}>{acct.name}</span>}
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{err.title}</div>
              <div style={{ fontSize: 12, color: C.textSec, marginBottom: 8, lineHeight: 1.5 }}>{err.description}</div>
              <div style={{ fontSize: 12, color: C.green, background: `${C.green}10`, borderRadius: 6, padding: "6px 10px" }}>💡 {err.recommendation}</div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── Scorecard View ──
  const renderScorecard = () => {
    const categories = [...new Set(scorecard.map(c => c.category))];
    return (
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Account Health Scorecard</div>
        <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
          {categories.map(cat => {
            const items = scorecard.filter(c => c.category === cat);
            const avg = Math.round(items.reduce((s, i) => s + i.score, 0) / items.length);
            return (
              <div key={cat} style={{ ...s.cardSm, flex: 1, textAlign: "center" }}>
                <HealthRing score={avg} size={52} strokeWidth={4} />
                <div style={{ fontSize: 12, fontWeight: 600, marginTop: 6 }}>{cat}</div>
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {scorecard.map((item, i) => (
            <div key={i} style={{ ...s.cardSm, display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 11, color: C.textDim, width: 130 }}>{item.category}</span>
              <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{item.criteria}</span>
              <MiniBar value={item.score} max={100} color={item.score >= 80 ? C.green : item.score >= 60 ? C.amber : C.red} height={6} />
              <span style={{ fontFamily: mono, fontSize: 12, width: 30, textAlign: "right", color: item.score >= 80 ? C.green : item.score >= 60 ? C.amber : C.red }}>{item.score}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── Reports View ──
  const renderReports = () => (
    <div>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Generate Report</div>
      <div style={s.card}>
        <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
          <select style={s.input}>
            <option>This Month</option><option>Last Month</option><option>Last 7 Days</option><option>Last 90 Days</option>
          </select>
          <select style={s.input}>
            <option value="">All Accounts</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <button style={s.btn} onClick={() => setReportGenerated(true)}>Generate Report</button>
        </div>
        {reportGenerated && (
          <div style={{ background: C.surface, borderRadius: 10, padding: 20 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>📊 Performance Report — February 2026</div>
            <div style={{ fontSize: 13, color: C.textSec, lineHeight: 1.7, marginBottom: 16 }}>
              Overall portfolio spend reached £{(totalSpend / 1000).toFixed(1)}k this period with {totalConversions.toLocaleString()} conversions at an average ROAS of {avgRoas}x. {accounts.filter(a => (a.health || 75) >= 80).length} of {accounts.length} accounts are in healthy standing. {pendingImps} improvements remain pending, including {filteredImps.filter(i => i.priority === "critical" || i.priority === "high").length} high-priority items requiring attention.
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {accounts.map(a => (
                <div key={a.id} style={{ ...s.cardSm, minWidth: 150, flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{a.name}</div>
                  <div style={{ fontSize: 11, color: C.textDim }}>Spend: £{(a.spend || 0).toLocaleString()}</div>
                  <div style={{ fontSize: 11, color: C.textDim }}>Conv: {a.conversions || 0}</div>
                  <div style={{ fontSize: 11, color: (a.roas || 0) >= 4 ? C.green : C.amber }}>ROAS: {a.roas || 0}x</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ── Accounts View ──
  const renderAccounts = () => (
    <div>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Client Accounts</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
        {accounts.map(a => {
          const acctImps = improvements.filter(i => String(i.accountId) === String(a.id) && !dismissed.has(i.id) && !applied.has(i.id));
          return (
            <div key={a.id} style={{ ...s.card, cursor: "pointer" }} onClick={() => { setSelectedAccount(String(a.id)); setView("dashboard"); }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{a.name}</div>
                  <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>{a.trend === "up" ? "▲ Trending up" : "▼ Trending down"}</div>
                </div>
                <HealthRing score={a.health || 75} size={40} strokeWidth={3} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12 }}>
                <div><span style={{ color: C.textDim }}>Spend </span><span style={{ fontFamily: mono, fontWeight: 600 }}>£{(a.spend || 0).toLocaleString()}</span></div>
                <div><span style={{ color: C.textDim }}>Conv </span><span style={{ fontFamily: mono, fontWeight: 600 }}>{a.conversions || 0}</span></div>
                <div><span style={{ color: C.textDim }}>CTR </span><span style={{ fontFamily: mono, fontWeight: 600 }}>{a.ctr || 0}%</span></div>
                <div><span style={{ color: C.textDim }}>ROAS </span><span style={{ fontFamily: mono, fontWeight: 600, color: (a.roas || 0) >= 4 ? C.green : C.amber }}>{a.roas || 0}x</span></div>
              </div>
              {acctImps.length > 0 && (
                <div style={{ marginTop: 10, fontSize: 11, color: C.accent, fontWeight: 600 }}>⚡ {acctImps.length} improvements pending</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── Search Terms View ──
  const renderSearchTerms = () => {
    const terms = [...DEMO_SEARCH_TERMS]
      .filter(t => stFilter === "all" || t.status === stFilter)
      .sort((a, b) => (b[stSort] || 0) - (a[stSort] || 0));
    const wastedSpend = DEMO_SEARCH_TERMS.filter(t => t.status === "wasted").reduce((s, t) => s + t.cost, 0);
    return (
      <div>
        <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <KpiCard label="Total Terms" value={DEMO_SEARCH_TERMS.length} color={C.text} />
          <KpiCard label="Wasted Spend" value={`£${wastedSpend.toFixed(0)}`} color={C.red} sub={`${DEMO_SEARCH_TERMS.filter(t => t.status === "wasted").length} terms`} />
          <KpiCard label="Review Needed" value={DEMO_SEARCH_TERMS.filter(t => t.status === "review").length} color={C.amber} />
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          {["all", "added", "wasted", "review"].map(f => (
            <button key={f} style={{ ...s.btnGhost, ...(stFilter === f ? { background: `${C.accent}20`, color: C.accent, borderColor: `${C.accent}40` } : {}) }} onClick={() => setStFilter(f)}>
              {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11, color: C.textDim }}>Sort:</span>
            <select style={{ ...s.input, width: 120, fontSize: 11, padding: "4px 8px" }} value={stSort} onChange={e => setStSort(e.target.value)}>
              <option value="cost">Spend</option><option value="clicks">Clicks</option><option value="conversions">Conversions</option><option value="ctr">CTR</option>
            </select>
          </div>
        </div>
        <div style={{ ...s.card, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {["Search Term", "Clicks", "Impr.", "CTR", "CPC", "Cost", "Conv.", "Conv Rate", "Status", ""].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: C.textDim, fontWeight: 600, fontSize: 10, textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {terms.map((t, i) => {
                const statusColor = t.status === "added" ? C.green : t.status === "wasted" ? C.red : C.amber;
                return (
                  <tr key={i} style={{ borderBottom: `1px solid ${C.border}08` }}>
                    <td style={{ padding: "8px 10px", fontWeight: 500, maxWidth: 200 }}>{t.term}</td>
                    <td style={{ padding: "8px 10px", fontFamily: mono }}>{t.clicks}</td>
                    <td style={{ padding: "8px 10px", fontFamily: mono, color: C.textDim }}>{t.impressions.toLocaleString()}</td>
                    <td style={{ padding: "8px 10px", fontFamily: mono }}>{t.ctr}%</td>
                    <td style={{ padding: "8px 10px", fontFamily: mono }}>£{t.cpc.toFixed(2)}</td>
                    <td style={{ padding: "8px 10px", fontFamily: mono, color: t.status === "wasted" ? C.red : C.text }}>£{t.cost.toFixed(2)}</td>
                    <td style={{ padding: "8px 10px", fontFamily: mono, color: t.conversions > 0 ? C.green : C.red }}>{t.conversions}</td>
                    <td style={{ padding: "8px 10px", fontFamily: mono }}>{t.convRate}%</td>
                    <td style={{ padding: "8px 10px" }}><span style={s.badge(statusColor)}>{t.status}</span></td>
                    <td style={{ padding: "8px 10px" }}>
                      {t.status === "wasted" && <button style={{ ...s.btn, padding: "3px 8px", fontSize: 10, background: C.red }} title="Add as negative keyword">− Neg</button>}
                      {t.status === "review" && <button style={{ ...s.btn, padding: "3px 8px", fontSize: 10 }} title="Add as keyword">+ Add</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const views = { dashboard: renderDashboard, opportunity: renderOpportunity, improvements: renderImprovements, performance: renderPerformance, segments: renderSegments, budgets: renderBudgets, searchTerms: renderSearchTerms, errors: renderErrors, scorecard: renderScorecard, reports: renderReports, accounts: renderAccounts };

  return (
    <div style={{ display: "flex", height: "100%" }}>
      {sidebar}
      <div style={{ flex: 1, padding: 20, overflowY: "auto" }}>
        <div style={{ maxWidth: 900 }}>
          {loading ? <div style={{ textAlign: "center", padding: 60, color: C.textDim }}>Loading...</div> : (views[view] || renderDashboard)()}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// FEEDFORGE MODULE
// ═══════════════════════════════════════════
function FeedForge() {
  const [view, setView] = useState("dashboard");
  const [products, setProducts] = useState(DEMO_PRODUCTS);
  const [rules, setRules] = useState(DEMO_FEED_RULES);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const prodRes = await api.getProducts();
        if (prodRes?.products) { setProducts(prodRes.products); setConnected(true); }
        const ruleRes = await api.getRules();
        if (ruleRes?.rules) setRules(ruleRes.rules);
      } catch (e) { console.warn("FeedForge API not available, using demo data"); }
      setLoading(false);
    })();
  }, []);

  const filteredProducts = products.filter(p =>
    !searchQ || p.title.toLowerCase().includes(searchQ.toLowerCase()) || (p.sku || "").toLowerCase().includes(searchQ.toLowerCase())
  );

  const activeProducts = products.filter(p => p.status === "active");
  const totalRevenue = products.reduce((s, p) => s + (p.revenue || 0), 0);
  const avgRoas = products.length ? (products.reduce((s, p) => s + (p.roas || 0), 0) / products.length).toFixed(1) : 0;
  const issueCount = products.filter(p => !p.gtin || p.stock === 0 || !p.category).length;

  const handleSync = async () => { setSyncing(true); await api.syncWoo(); setSyncing(false); };
  const handleExport = async () => { setExporting(true); await api.exportXml(); setExporting(false); };
  const handleRunRules = async () => { await api.executeRules(); };

  const NAV = [
    { id: "dashboard", label: "Dashboard", icon: "◉" },
    { id: "products", label: "Product Feed", icon: "☰", count: products.length },
    { id: "rules", label: "Feed Rules", icon: "⚙", count: rules.filter(r => r.enabled).length },
    { id: "audit", label: "Feed Audit", icon: "🔍" },
    { id: "export", label: "Export Feed", icon: "↗" },
  ];

  const sidebar = (
    <div style={{ width: 200, borderRight: `1px solid ${C.border}`, padding: "16px 8px", display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 }}>
      <div style={{ padding: "0 12px 12px", fontSize: 15, fontWeight: 700, color: C.cyan, display: "flex", alignItems: "center", gap: 8 }}>🛒 FeedForge</div>
      {NAV.map(n => (
        <div key={n.id} style={s.sidebarItem(view === n.id)} onClick={() => setView(n.id)}>
          <span style={{ width: 18, textAlign: "center", fontSize: 13 }}>{n.icon}</span>
          <span>{n.label}</span>
          {n.count ? <span style={{ marginLeft: "auto", background: C.cyan, color: "#000", borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>{n.count}</span> : null}
        </div>
      ))}
    </div>
  );

  const renderDashboard = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <ConnectionBanner connected={connected} />
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <KpiCard label="Active Products" value={activeProducts.length} sub={`${products.length} total`} color={C.cyan} />
        <KpiCard label="Revenue" value={`£${(totalRevenue / 1000).toFixed(1)}k`} sub="+14%" trend="up" color={C.green} />
        <KpiCard label="Avg ROAS" value={`${avgRoas}x`} color={C.purple} />
        <KpiCard label="Feed Issues" value={issueCount} color={issueCount > 0 ? C.amber : C.green} />
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        <button style={{ ...s.btn, background: C.cyan, flex: 1, opacity: syncing ? 0.7 : 1 }} onClick={handleSync} disabled={syncing}>{syncing ? "Syncing..." : "↻ Sync WooCommerce"}</button>
        <button style={{ ...s.btn, flex: 1 }} onClick={handleRunRules}>⚙ Run Feed Rules</button>
        <button style={{ ...s.btn, background: C.green, flex: 1, opacity: exporting ? 0.7 : 1 }} onClick={handleExport} disabled={exporting}>{exporting ? "Exporting..." : "↗ Export XML Feed"}</button>
      </div>
      <div style={s.card}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Top Revenue Products</div>
        {[...products].sort((a, b) => (b.revenue || 0) - (a.revenue || 0)).slice(0, 5).map((p, i) => (
          <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
            <span style={{ fontFamily: mono, fontSize: 11, color: C.textDim, width: 20 }}>#{i + 1}</span>
            <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{p.title}</span>
            <span style={{ fontFamily: mono, fontSize: 12, color: C.green }}>£{(p.revenue || 0).toLocaleString()}</span>
            <span style={{ fontFamily: mono, fontSize: 11, color: C.textDim }}>{p.roas || 0}x</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderProducts = () => (
    <div>
      <div style={{ marginBottom: 12 }}>
        <input style={{ ...s.input, maxWidth: 300 }} placeholder="Search products..." value={searchQ} onChange={e => setSearchQ(e.target.value)} />
      </div>
      <div style={{ ...s.card, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {["Product", "SKU", "Price", "Stock", "ROAS", "Status", "Issues"].map(h => (
                <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: C.textDim, fontWeight: 600, fontSize: 10, textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map(p => {
              const issues = [];
              if (!p.gtin) issues.push("No GTIN");
              if (p.stock === 0) issues.push("OOS");
              if (!p.category) issues.push("No category");
              return (
                <tr key={p.id} style={{ borderBottom: `1px solid ${C.border}08` }}>
                  <td style={{ padding: "8px 10px", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 500 }}>{p.title}</td>
                  <td style={{ padding: "8px 10px", fontFamily: mono, color: C.textDim }}>{p.sku}</td>
                  <td style={{ padding: "8px 10px", fontFamily: mono }}>{p.salePrice ? <><span style={{ textDecoration: "line-through", color: C.textDim }}>£{p.price}</span> <span style={{ color: C.green }}>£{p.salePrice}</span></> : `£${p.price}`}</td>
                  <td style={{ padding: "8px 10px", fontFamily: mono, color: p.stock === 0 ? C.red : p.stock < 10 ? C.amber : C.text }}>{p.stock}</td>
                  <td style={{ padding: "8px 10px", fontFamily: mono, color: (p.roas || 0) >= 4 ? C.green : (p.roas || 0) >= 2 ? C.text : C.red }}>{p.roas || 0}x</td>
                  <td style={{ padding: "8px 10px" }}><span style={s.badge(p.status === "active" ? C.green : C.red)}>{p.status}</span></td>
                  <td style={{ padding: "8px 10px" }}>{issues.length > 0 ? issues.map((iss, i) => <span key={i} style={{ ...s.badge(C.amber), marginRight: 4 }}>{iss}</span>) : <span style={{ color: C.green }}>✓</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderRules = () => (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>Feed Rules</div>
        <button style={s.btn} onClick={handleRunRules}>▶ Run All Rules</button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {rules.map(rule => (
          <div key={rule.id} style={{ ...s.card, borderLeft: `3px solid ${rule.enabled ? C.green : C.textDim}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>{rule.name}</span>
              <span style={s.badge(rule.enabled ? C.green : C.textDim)}>{rule.enabled ? "Active" : "Disabled"}</span>
            </div>
            <div style={{ fontSize: 12, color: C.textSec, marginBottom: 6 }}>
              IF {rule.conditions.map(c => `${c.field} ${c.op} ${c.value || ""}`).join(" AND ")} → {rule.actions.map(a => `${a.type} ${a.field || ""} ${a.value || ""}`).join(", ")}
            </div>
            <div style={{ fontSize: 11, color: C.textDim }}>Last run: {rule.lastRun} · {rule.affected} products affected</div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAudit = () => {
    const checks = [
      { label: "Missing GTIN", count: products.filter(p => !p.gtin).length, severity: "high" },
      { label: "Out of stock (active)", count: products.filter(p => p.stock === 0 && p.status === "active").length, severity: "critical" },
      { label: "Missing category", count: products.filter(p => !p.category).length, severity: "medium" },
      { label: "No sale price set", count: products.filter(p => !p.salePrice).length, severity: "low" },
      { label: "Low ROAS (<2x)", count: products.filter(p => (p.roas || 0) < 2 && (p.roas || 0) > 0).length, severity: "medium" },
      { label: "Budget wasters (0 conv)", count: products.filter(p => (p.clicks || 0) > 50 && (p.conversions || 0) === 0).length, severity: "high" },
    ];
    return (
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Feed Quality Audit</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {checks.map((ch, i) => (
            <div key={i} style={{ ...s.card, display: "flex", alignItems: "center", gap: 12 }}>
              <PriorityBadge priority={ch.severity} />
              <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{ch.label}</span>
              <span style={{ fontFamily: mono, fontSize: 18, fontWeight: 700, color: ch.count > 0 ? C.amber : C.green }}>{ch.count}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderExport = () => (
    <div>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Export Feed</div>
      <div style={{ ...s.card, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: 13, color: C.textSec }}>Generate your product feed for Google Merchant Center. The XML feed URL can be submitted directly to GMC for automatic updates.</div>
        <div style={{ display: "flex", gap: 12 }}>
          <button style={{ ...s.btn, background: C.green }} onClick={handleExport}>↗ Export Google Shopping XML</button>
          <button style={s.btnGhost}>↗ Export JSON</button>
          <button style={s.btnGhost}>↗ Export CSV</button>
        </div>
        <div style={{ background: C.surface, borderRadius: 8, padding: 12, fontFamily: mono, fontSize: 12, color: C.textDim }}>
          Feed URL: {API_BASE}/api/feed/export/google.xml
        </div>
        <div style={{ fontSize: 12, color: C.textDim }}>
          Active products: {activeProducts.length} · Last export: {connected ? "Check server" : "N/A"} · Format: RSS 2.0 with g: namespace
        </div>
      </div>
    </div>
  );

  const views = { dashboard: renderDashboard, products: renderProducts, rules: renderRules, audit: renderAudit, export: renderExport };

  return (
    <div style={{ display: "flex", height: "100%" }}>
      {sidebar}
      <div style={{ flex: 1, padding: 20, overflowY: "auto" }}>
        <div style={{ maxWidth: 900 }}>
          {loading ? <div style={{ textAlign: "center", padding: 60, color: C.textDim }}>Loading...</div> : (views[view] || renderDashboard)()}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// ACTIVITY FEED MODULE
// ═══════════════════════════════════════════
function ActivityFeed() {
  const [entries, setEntries] = useState(DEMO_ACTIVITY);
  const [filter, setFilter] = useState("all");
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getActivity({ limit: 50 });
        if (res?.entries) { setEntries(res.entries); setConnected(true); }
      } catch (e) { console.warn("Activity API not available, using demo data"); }
    })();
  }, []);

  const filtered = filter === "all" ? entries : entries.filter(e => e.module === filter);

  const actionIcons = {
    improvement_applied: "⚡", improvement_dismissed: "✕", bid_changed: "↕",
    ad_paused: "⏸", negative_keyword_added: "−", budget_changed: "£",
    rule_executed: "⚙", products_excluded: "✕", feed_exported: "↗", product_sync: "↻",
  };
  const actionColors = {
    improvement_applied: C.green, improvement_dismissed: C.textDim, bid_changed: C.amber,
    ad_paused: C.red, negative_keyword_added: C.purple, budget_changed: C.cyan,
    rule_executed: C.blue, products_excluded: C.red, feed_exported: C.green, product_sync: C.cyan,
  };

  return (
    <div style={{ padding: 20, maxWidth: 700 }}>
      <ConnectionBanner connected={connected} />
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {["all", "ppc", "feedforge"].map(f => (
          <button key={f} style={{ ...s.btnGhost, ...(filter === f ? { background: `${C.accent}20`, color: C.accent, borderColor: `${C.accent}40` } : {}) }} onClick={() => setFilter(f)}>
            {f === "all" ? "All Activity" : f === "ppc" ? "PPC Pulse" : "FeedForge"}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {filtered.map((entry, i) => (
          <div key={entry.id || i} style={{ display: "flex", gap: 12, padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${actionColors[entry.action] || C.accent}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>
              {actionIcons[entry.action] || "•"}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                <span style={{ fontWeight: 600 }}>{entry.user}</span> · {entry.summary}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 4, alignItems: "center" }}>
                <span style={s.badge(entry.module === "ppc" ? C.accent : C.cyan)}>{entry.module === "ppc" ? "PPC Pulse" : "FeedForge"}</span>
                {entry.impact && <span style={{ fontSize: 11, color: C.green, fontWeight: 600 }}>{entry.impact}</span>}
                <span style={{ fontSize: 11, color: C.textDim, marginLeft: "auto" }}>{entry.time}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// AI CAMPAIGN BUILDER
// ═══════════════════════════════════════════
function AICampaignBuilder() {
  const [tab, setTab] = useState("brief");
  const [brief, setBrief] = useState({ business: "", website: "", goals: "leads", budget: "2000", location: "United Kingdom", audience: "", competitors: "", products: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [pushAccount, setPushAccount] = useState("");
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [pushError, setPushError] = useState("");
  const [pushAccounts, setPushAccounts] = useState(DEMO_ACCOUNTS);

  useEffect(() => { (async () => { try { const res = await api.getAccounts(); if (res?.accounts) setPushAccounts(res.accounts); } catch(e) {} })(); }, []);

  const handleGenerate = async () => {
    if (!brief.business) { setError("Please enter a business name"); return; }
    setLoading(true); setError(""); setResult(null);
    try {
      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-5-20250514",
          max_tokens: 4000,
          messages: [{ role: "user", content: `You are a Google Ads expert building a campaign structure for an agency client. Respond ONLY in valid JSON (no markdown, no backticks, no preamble).

Client Brief:
- Business: ${brief.business}
- Website: ${brief.website || "Not provided"}
- Goal: ${brief.goals}
- Monthly Budget: £${brief.budget}
- Target Location: ${brief.location}
- Target Audience: ${brief.audience || "Not specified"}
- Key Products/Services: ${brief.products || "Not specified"}
- Competitors: ${brief.competitors || "Not specified"}

Generate a complete Google Ads campaign structure. Return this exact JSON structure:
{
  "summary": "2-3 sentence strategy overview",
  "bidStrategy": "recommended bid strategy name",
  "bidRationale": "why this bid strategy",
  "monthlyBudget": ${brief.budget},
  "campaigns": [
    {
      "name": "campaign name",
      "type": "Search|Shopping|Display|Performance Max",
      "dailyBudget": number,
      "budgetPercent": number,
      "rationale": "why this campaign",
      "adGroups": [
        {
          "name": "ad group name",
          "theme": "theme description",
          "keywords": [
            { "keyword": "keyword text", "matchType": "Exact|Phrase|Broad", "intent": "High|Medium|Low", "estimatedCpc": number }
          ],
          "negativeKeywords": ["negative keyword 1"],
          "ads": [
            {
              "headlines": ["h1", "h2", "h3", "h4", "h5", "h6", "h7", "h8", "h9", "h10", "h11", "h12", "h13", "h14", "h15"],
              "descriptions": ["d1", "d2", "d3", "d4"]
            }
          ]
        }
      ]
    }
  ],
  "sharedNegatives": ["negative keyword list"],
  "extensions": { "sitelinks": [{"text":"text","url":"url"}], "callouts": ["callout"], "snippets": [{"header":"header","values":["v1","v2"]}] },
  "tips": ["implementation tip 1", "tip 2", "tip 3"]
}

Create 2-4 campaigns with 2-4 ad groups each. Include 8-15 keywords per ad group. Generate realistic RSA headlines (max 30 chars each) and descriptions (max 90 chars each). Use GBP for all monetary values.` }],
        }),
      });
      const data = await response.json();
      const text = data.content?.map(b => b.text || "").join("") || "";
      const clean = text.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setResult(parsed);
      setTab("result");
    } catch (err) {
      setError("Failed to generate campaign. Check your connection and try again.");
      console.error(err);
    }
    setLoading(false);
  };

  const NAV = [
    { id: "brief", label: "Client Brief", icon: "📝" },
    { id: "result", label: "Campaign Plan", icon: "🎯", disabled: !result },
    { id: "export", label: "Export", icon: "↗", disabled: !result },
  ];

  const sidebar = (
    <div style={{ width: 200, borderRight: `1px solid ${C.border}`, padding: "16px 8px", flexShrink: 0 }}>
      <div style={{ padding: "0 12px 12px", fontSize: 15, fontWeight: 700, color: C.pink, display: "flex", alignItems: "center", gap: 8 }}>🤖 Campaign AI</div>
      {NAV.map(n => (
        <div key={n.id} style={{ ...s.sidebarItem(tab === n.id), opacity: n.disabled ? 0.4 : 1, pointerEvents: n.disabled ? "none" : "auto" }} onClick={() => setTab(n.id)}>
          <span style={{ width: 18, textAlign: "center" }}>{n.icon}</span><span>{n.label}</span>
        </div>
      ))}
    </div>
  );

  const renderBrief = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ fontSize: 16, fontWeight: 700 }}>Client Campaign Brief</div>
      <div style={{ fontSize: 13, color: C.textSec }}>Fill in the details below and AI will generate a complete Google Ads campaign structure including keywords, ad copy, budget allocation, and negative keyword lists.</div>
      {error && <div style={{ background: `${C.red}15`, border: `1px solid ${C.red}30`, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: C.red }}>{error}</div>}
      <div style={s.card}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div style={{ gridColumn: "1 / -1" }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 4 }}>Business Name *</label><input style={s.input} value={brief.business} onChange={e => setBrief(p => ({ ...p, business: e.target.value }))} placeholder="e.g. Apex Fitness Co." /></div>
          <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 4 }}>Website</label><input style={s.input} value={brief.website} onChange={e => setBrief(p => ({ ...p, website: e.target.value }))} placeholder="https://apexfitness.co.uk" /></div>
          <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 4 }}>Campaign Goal</label>
            <select style={s.input} value={brief.goals} onChange={e => setBrief(p => ({ ...p, goals: e.target.value }))}>
              <option value="leads">Lead Generation</option><option value="sales">Online Sales</option><option value="calls">Phone Calls</option><option value="traffic">Website Traffic</option><option value="brand">Brand Awareness</option><option value="local">Local Visits</option>
            </select>
          </div>
          <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 4 }}>Monthly Budget (£)</label><input style={s.input} type="number" value={brief.budget} onChange={e => setBrief(p => ({ ...p, budget: e.target.value }))} /></div>
          <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 4 }}>Target Location</label><input style={s.input} value={brief.location} onChange={e => setBrief(p => ({ ...p, location: e.target.value }))} placeholder="e.g. London, Manchester, UK-wide" /></div>
          <div style={{ gridColumn: "1 / -1" }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 4 }}>Key Products / Services</label><textarea style={{ ...s.input, minHeight: 60, resize: "vertical" }} value={brief.products} onChange={e => setBrief(p => ({ ...p, products: e.target.value }))} placeholder="e.g. Personal training, group classes, nutrition coaching, online programmes" /></div>
          <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 4 }}>Target Audience</label><input style={s.input} value={brief.audience} onChange={e => setBrief(p => ({ ...p, audience: e.target.value }))} placeholder="e.g. 25-45 professionals" /></div>
          <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 4 }}>Competitors</label><input style={s.input} value={brief.competitors} onChange={e => setBrief(p => ({ ...p, competitors: e.target.value }))} placeholder="e.g. PureGym, David Lloyd" /></div>
        </div>
        <button style={{ ...s.btn, marginTop: 20, padding: "12px 24px", fontSize: 14, background: `linear-gradient(135deg, ${C.accent}, ${C.pink})`, width: "100%", opacity: loading ? 0.7 : 1 }} onClick={handleGenerate} disabled={loading}>
          {loading ? "🤖 Generating campaign structure..." : "🤖 Generate Campaign with AI"}
        </button>
      </div>
    </div>
  );

  const renderResult = () => {
    if (!result) return null;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ ...s.card, borderLeft: `3px solid ${C.accent}` }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>Strategy Summary</div>
          <div style={{ fontSize: 13, color: C.textSec, lineHeight: 1.6 }}>{result.summary}</div>
          <div style={{ display: "flex", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
            <KpiCard label="Monthly Budget" value={`£${result.monthlyBudget?.toLocaleString() || brief.budget}`} color={C.text} />
            <KpiCard label="Campaigns" value={result.campaigns?.length || 0} color={C.accent} />
            <KpiCard label="Ad Groups" value={result.campaigns?.reduce((s, c) => s + (c.adGroups?.length || 0), 0) || 0} color={C.blue} />
            <KpiCard label="Bid Strategy" value={result.bidStrategy || "tCPA"} color={C.green} />
          </div>
        </div>

        {result.campaigns?.map((campaign, ci) => (
          <div key={ci} style={s.card}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => setExpandedCampaign(expandedCampaign === ci ? null : ci)}>
              <span style={{ fontSize: 18 }}>{expandedCampaign === ci ? "▾" : "▸"}</span>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 700 }}>{campaign.name}</span>
                  <span style={s.badge(C.blue)}>{campaign.type}</span>
                </div>
                <div style={{ fontSize: 12, color: C.textDim, marginTop: 2 }}>{campaign.rationale}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: mono, fontSize: 14, fontWeight: 700 }}>£{campaign.dailyBudget}/day</div>
                <div style={{ fontSize: 11, color: C.textDim }}>{campaign.budgetPercent}% of budget</div>
              </div>
            </div>

            {expandedCampaign === ci && (
              <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 14 }}>
                {campaign.adGroups?.map((ag, agi) => (
                  <div key={agi} style={{ background: C.surface, borderRadius: 10, padding: 14, border: `1px solid ${C.border}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>{ag.name}</span>
                      <span style={{ fontSize: 11, color: C.textDim }}>· {ag.theme}</span>
                    </div>

                    {/* Keywords */}
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Keywords ({ag.keywords?.length})</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {ag.keywords?.map((kw, ki) => (
                          <span key={ki} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, background: kw.intent === "High" ? `${C.green}15` : kw.intent === "Medium" ? `${C.amber}15` : `${C.textDim}10`, color: kw.intent === "High" ? C.green : kw.intent === "Medium" ? C.amber : C.textSec, border: `1px solid ${kw.intent === "High" ? C.green : kw.intent === "Medium" ? C.amber : C.textDim}20` }}>
                            {kw.matchType === "Exact" ? `[${kw.keyword}]` : kw.matchType === "Phrase" ? `"${kw.keyword}"` : kw.keyword}
                            {kw.estimatedCpc ? <span style={{ marginLeft: 4, fontFamily: mono, fontSize: 10, opacity: 0.7 }}>~£{kw.estimatedCpc}</span> : null}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Negative Keywords */}
                    {ag.negativeKeywords?.length > 0 && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: C.red, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Negatives</div>
                        <div style={{ fontSize: 11, color: C.textDim }}>{ag.negativeKeywords.join(", ")}</div>
                      </div>
                    )}

                    {/* Ads */}
                    {ag.ads?.map((ad, adi) => (
                      <div key={adi} style={{ marginTop: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: C.purple, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>RSA Ad Copy</div>
                        <div style={{ background: C.bg, borderRadius: 8, padding: 10, border: `1px solid ${C.border}` }}>
                          <div style={{ fontSize: 11, color: C.textDim, marginBottom: 4 }}>Headlines ({ad.headlines?.length}/15):</div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                            {ad.headlines?.map((h, hi) => <span key={hi} style={{ fontSize: 11, padding: "2px 6px", background: `${C.accent}10`, borderRadius: 3, color: C.accentLight }}>{h}</span>)}
                          </div>
                          <div style={{ fontSize: 11, color: C.textDim, marginTop: 8, marginBottom: 4 }}>Descriptions ({ad.descriptions?.length}/4):</div>
                          {ad.descriptions?.map((d, di) => <div key={di} style={{ fontSize: 12, color: C.textSec, marginBottom: 2 }}>{d}</div>)}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Shared Negatives */}
        {result.sharedNegatives?.length > 0 && (
          <div style={s.card}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Shared Negative Keywords</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {result.sharedNegatives.map((n, i) => <span key={i} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, background: `${C.red}10`, color: C.red }}>−{n}</span>)}
            </div>
          </div>
        )}

        {/* Extensions */}
        {result.extensions && (
          <div style={s.card}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Ad Extensions</div>
            {result.extensions.sitelinks?.length > 0 && <div style={{ marginBottom: 8 }}><div style={{ fontSize: 11, fontWeight: 700, color: C.blue, marginBottom: 4 }}>SITELINKS</div>{result.extensions.sitelinks.map((sl, i) => <div key={i} style={{ fontSize: 12, color: C.textSec }}><span style={{ color: C.accent }}>{sl.text}</span> → {sl.url}</div>)}</div>}
            {result.extensions.callouts?.length > 0 && <div style={{ marginBottom: 8 }}><div style={{ fontSize: 11, fontWeight: 700, color: C.green, marginBottom: 4 }}>CALLOUTS</div><div style={{ fontSize: 12, color: C.textSec }}>{result.extensions.callouts.join(" · ")}</div></div>}
          </div>
        )}

        {/* Tips */}
        {result.tips?.length > 0 && (
          <div style={s.card}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>💡 Implementation Tips</div>
            {result.tips.map((tip, i) => <div key={i} style={{ fontSize: 12, color: C.textSec, padding: "4px 0", borderBottom: `1px solid ${C.border}08` }}>{i + 1}. {tip}</div>)}
          </div>
        )}
      </div>
    );
  };

  const renderExport = () => {
    if (!result) return null;
    const totalKeywords = result.campaigns?.reduce((s, c) => s + c.adGroups?.reduce((s2, ag) => s2 + (ag.keywords?.length || 0), 0), 0) || 0;
    const accounts = pushAccounts;

    const handlePush = async () => {
      if (!pushAccount) { setPushError("Select a client account first"); return; }
      setPushing(true); setPushError(""); setPushResult(null);
      try {
        const res = await api.pushCampaignToAds(pushAccount, { ...result, website: brief.website });
        if (res?.success) { setPushResult(res); }
        else { setPushError(res?.error || "Failed to push — is your backend connected and Google Ads API configured?"); }
      } catch (err) { setPushError("Backend not reachable. Connect your backend first, then retry."); }
      setPushing(false);
    };

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>Export & Deploy Campaign</div>

        {/* Push to Google Ads */}
        <div style={{ ...s.card, borderLeft: `3px solid ${C.green}` }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>🚀 Push to Google Ads (PAUSED)</div>
          <div style={{ fontSize: 12, color: C.textSec, marginBottom: 12 }}>Creates the full campaign structure directly in your client's Google Ads account. Everything is created as <strong style={{ color: C.amber }}>PAUSED</strong> — campaigns, ad groups, and ads all need manual activation. Keywords are added but won't serve until you enable the parent campaign.</div>
          {pushError && <div style={{ background: `${C.red}15`, border: `1px solid ${C.red}30`, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: C.red, marginBottom: 12 }}>{pushError}</div>}
          {pushResult && (
            <div style={{ background: `${C.green}10`, border: `1px solid ${C.green}30`, borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.green, marginBottom: 6 }}>✓ Campaign structure created successfully!</div>
              <div style={{ fontSize: 12, color: C.textSec }}>
                {pushResult.summary?.campaignsCreated} campaigns · {pushResult.summary?.adGroupsCreated} ad groups · {pushResult.summary?.keywordsAdded} keywords · {pushResult.summary?.adsCreated} ads
                {pushResult.summary?.errorsEncountered > 0 && <span style={{ color: C.amber }}> · {pushResult.summary.errorsEncountered} warnings</span>}
              </div>
              <div style={{ fontSize: 11, color: C.amber, marginTop: 6, fontWeight: 600 }}>Status: ALL PAUSED — go to PPC Pulse or Google Ads to review and activate</div>
            </div>
          )}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select style={{ ...s.input, width: 250 }} value={pushAccount} onChange={e => setPushAccount(e.target.value)}>
              <option value="">Select client account...</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.id})</option>)}
            </select>
            <button style={{ ...s.btn, background: C.green, padding: "8px 20px", opacity: pushing ? 0.7 : 1 }} onClick={handlePush} disabled={pushing || !!pushResult}>
              {pushing ? "Creating..." : pushResult ? "✓ Deployed" : "🚀 Push to Google Ads"}
            </button>
          </div>
        </div>

        {/* File Exports */}
        <div style={s.card}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>File Exports</div>
          <div style={{ fontSize: 13, color: C.textSec, marginBottom: 12 }}>Your plan has {result.campaigns?.length} campaigns, {result.campaigns?.reduce((s, c) => s + (c.adGroups?.length || 0), 0)} ad groups, and {totalKeywords} keywords.</div>
          <div style={{ display: "flex", gap: 12 }}>
            <button style={s.btn} onClick={() => { const blob = new Blob([JSON.stringify(result, null, 2)], { type: "application/json" }); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `campaign-${brief.business.replace(/\s+/g, "-").toLowerCase()}.json`; a.click(); }}>↗ Export JSON</button>
            <button style={s.btnGhost} onClick={() => {
              let csv = "Campaign,Ad Group,Keyword,Match Type,Intent,Est CPC\n";
              result.campaigns?.forEach(c => c.adGroups?.forEach(ag => ag.keywords?.forEach(kw => { csv += `"${c.name}","${ag.name}","${kw.keyword}","${kw.matchType}","${kw.intent}",${kw.estimatedCpc || ""}\n`; })));
              const blob = new Blob([csv], { type: "text/csv" }); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `keywords-${brief.business.replace(/\s+/g, "-").toLowerCase()}.csv`; a.click();
            }}>↗ Export Keywords CSV</button>
            <button style={s.btnGhost} onClick={() => navigator.clipboard?.writeText(JSON.stringify(result, null, 2))}>📋 Copy JSON</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: "flex", height: "100%" }}>
      {sidebar}
      <div style={{ flex: 1, padding: 20, overflowY: "auto" }}>
        <div style={{ maxWidth: 900 }}>
          {tab === "brief" && renderBrief()}
          {tab === "result" && renderResult()}
          {tab === "export" && renderExport()}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// AI KEYWORD RESEARCH
// ═══════════════════════════════════════════
function AIKeywordResearch() {
  const [tab, setTab] = useState("research");
  const [seed, setSeed] = useState("");
  const [industry, setIndustry] = useState("");
  const [location, setLocation] = useState("United Kingdom");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [savedKeywords, setSavedKeywords] = useState([]);

  const handleResearch = async () => {
    if (!seed) { setError("Please enter a seed keyword or business description"); return; }
    setLoading(true); setError(""); setResult(null);
    try {
      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-5-20250514",
          max_tokens: 4000,
          messages: [{ role: "user", content: `You are a PPC keyword research expert. Respond ONLY in valid JSON (no markdown, no backticks, no preamble).

Research brief:
- Seed keyword/topic: ${seed}
- Industry: ${industry || "Not specified"}
- Target location: ${location}

Perform comprehensive keyword research and return this exact JSON:
{
  "overview": "2-3 sentence summary of the keyword landscape",
  "totalKeywords": number,
  "estimatedMonthlySearches": number,
  "avgCpc": number,
  "clusters": [
    {
      "name": "cluster theme name",
      "intent": "Informational|Navigational|Commercial|Transactional",
      "priority": "High|Medium|Low",
      "keywords": [
        {
          "keyword": "keyword text",
          "volume": number,
          "cpc": number,
          "competition": "High|Medium|Low",
          "matchType": "Exact|Phrase|Broad",
          "intent": "Informational|Navigational|Commercial|Transactional"
        }
      ]
    }
  ],
  "negativeKeywords": [
    { "keyword": "negative keyword", "reason": "why to exclude" }
  ],
  "longTail": [
    { "keyword": "long tail keyword", "volume": number, "cpc": number, "opportunity": "High|Medium" }
  ],
  "questions": [
    { "question": "question keyword", "volume": number, "adOpportunity": "Use in RSA headline|Target with FAQ page|Skip" }
  ],
  "seasonalNotes": "any seasonal trends to be aware of",
  "competitorKeywords": ["keyword competitors likely bid on"],
  "tips": ["strategic tip 1", "tip 2", "tip 3"]
}

Generate 5-8 keyword clusters with 8-15 keywords each. Include realistic UK search volumes and CPC estimates in GBP. Be comprehensive and creative with long-tail variations, question keywords, and competitor insights.` }],
        }),
      });
      const data = await response.json();
      const text = data.content?.map(b => b.text || "").join("") || "";
      const clean = text.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setResult(parsed);
      setTab("clusters");
    } catch (err) {
      setError("Failed to generate research. Check your connection and try again.");
      console.error(err);
    }
    setLoading(false);
  };

  const toggleSaveKeyword = (kw) => {
    setSavedKeywords(prev => prev.find(k => k.keyword === kw.keyword) ? prev.filter(k => k.keyword !== kw.keyword) : [...prev, kw]);
  };

  const NAV = [
    { id: "research", label: "Research", icon: "🔍" },
    { id: "clusters", label: "Clusters", icon: "◧", disabled: !result },
    { id: "negatives", label: "Negatives", icon: "−", disabled: !result },
    { id: "longtail", label: "Long Tail", icon: "🎯", disabled: !result },
    { id: "questions", label: "Questions", icon: "❓", disabled: !result },
    { id: "saved", label: "Saved", icon: "★", count: savedKeywords.length },
  ];

  const sidebar = (
    <div style={{ width: 200, borderRight: `1px solid ${C.border}`, padding: "16px 8px", flexShrink: 0 }}>
      <div style={{ padding: "0 12px 12px", fontSize: 15, fontWeight: 700, color: C.green, display: "flex", alignItems: "center", gap: 8 }}>🔬 Keyword AI</div>
      {NAV.map(n => (
        <div key={n.id} style={{ ...s.sidebarItem(tab === n.id), opacity: n.disabled ? 0.4 : 1, pointerEvents: n.disabled ? "none" : "auto" }} onClick={() => setTab(n.id)}>
          <span style={{ width: 18, textAlign: "center" }}>{n.icon}</span><span>{n.label}</span>
          {n.count ? <span style={{ marginLeft: "auto", background: C.green, color: "#000", borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>{n.count}</span> : null}
        </div>
      ))}
    </div>
  );

  const renderResearch = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ fontSize: 16, fontWeight: 700 }}>AI Keyword Research</div>
      <div style={{ fontSize: 13, color: C.textSec }}>Enter a seed keyword or business description and AI will generate keyword clusters, search volume estimates, CPC data, negative keywords, long-tail opportunities, and question keywords.</div>
      {error && <div style={{ background: `${C.red}15`, border: `1px solid ${C.red}30`, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: C.red }}>{error}</div>}
      <div style={s.card}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 4 }}>Seed Keyword or Business Description *</label><textarea style={{ ...s.input, minHeight: 60, resize: "vertical" }} value={seed} onChange={e => setSeed(e.target.value)} placeholder="e.g. personal trainer London, or 'boutique fitness studio offering PT, group classes and nutrition coaching'" /></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 4 }}>Industry</label><input style={s.input} value={industry} onChange={e => setIndustry(e.target.value)} placeholder="e.g. Fitness, Dental, Legal, SaaS" /></div>
            <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 4 }}>Target Location</label><input style={s.input} value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. London, UK-wide" /></div>
          </div>
        </div>
        <button style={{ ...s.btn, marginTop: 20, padding: "12px 24px", fontSize: 14, background: `linear-gradient(135deg, ${C.green}, ${C.cyan})`, width: "100%", opacity: loading ? 0.7 : 1 }} onClick={handleResearch} disabled={loading}>
          {loading ? "🔬 Researching keywords..." : "🔬 Research Keywords with AI"}
        </button>
      </div>
    </div>
  );

  const renderClusters = () => {
    if (!result) return null;
    const intentColor = { Transactional: C.green, Commercial: C.blue, Navigational: C.purple, Informational: C.amber };
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ ...s.card, borderLeft: `3px solid ${C.green}` }}>
          <div style={{ fontSize: 13, color: C.textSec, lineHeight: 1.6 }}>{result.overview}</div>
          <div style={{ display: "flex", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
            <KpiCard label="Keywords Found" value={result.totalKeywords || "—"} color={C.green} />
            <KpiCard label="Est. Monthly Searches" value={(result.estimatedMonthlySearches || 0).toLocaleString()} color={C.blue} />
            <KpiCard label="Avg CPC" value={`£${result.avgCpc?.toFixed(2) || "—"}`} color={C.amber} />
            <KpiCard label="Clusters" value={result.clusters?.length || 0} color={C.purple} />
          </div>
        </div>
        {result.clusters?.map((cluster, ci) => (
          <div key={ci} style={s.card}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, cursor: "pointer" }} onClick={() => setSelectedCluster(selectedCluster === ci ? null : ci)}>
              <span style={{ fontSize: 16 }}>{selectedCluster === ci ? "▾" : "▸"}</span>
              <span style={{ fontSize: 14, fontWeight: 700, flex: 1 }}>{cluster.name}</span>
              <span style={s.badge(intentColor[cluster.intent] || C.textDim)}>{cluster.intent}</span>
              <span style={s.badge(cluster.priority === "High" ? C.green : cluster.priority === "Medium" ? C.amber : C.textDim)}>{cluster.priority}</span>
              <span style={{ fontFamily: mono, fontSize: 12, color: C.textDim }}>{cluster.keywords?.length} kw</span>
            </div>
            {selectedCluster === ci && (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginTop: 8 }}>
                  <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>
                    {["", "Keyword", "Volume", "CPC", "Competition", "Match", "Intent"].map(h => <th key={h} style={{ textAlign: "left", padding: "6px 8px", color: C.textDim, fontSize: 10, fontWeight: 600, textTransform: "uppercase" }}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {cluster.keywords?.map((kw, ki) => {
                      const isSaved = savedKeywords.find(k => k.keyword === kw.keyword);
                      return (
                        <tr key={ki} style={{ borderBottom: `1px solid ${C.border}08` }}>
                          <td style={{ padding: "6px 8px" }}><span onClick={() => toggleSaveKeyword(kw)} style={{ cursor: "pointer", fontSize: 14 }}>{isSaved ? "★" : "☆"}</span></td>
                          <td style={{ padding: "6px 8px", fontWeight: 500 }}>{kw.matchType === "Exact" ? `[${kw.keyword}]` : kw.matchType === "Phrase" ? `"${kw.keyword}"` : kw.keyword}</td>
                          <td style={{ padding: "6px 8px", fontFamily: mono }}>{(kw.volume || 0).toLocaleString()}</td>
                          <td style={{ padding: "6px 8px", fontFamily: mono }}>£{kw.cpc?.toFixed(2) || "—"}</td>
                          <td style={{ padding: "6px 8px" }}><span style={s.badge(kw.competition === "High" ? C.red : kw.competition === "Medium" ? C.amber : C.green)}>{kw.competition}</span></td>
                          <td style={{ padding: "6px 8px" }}><span style={s.badge(C.blue)}>{kw.matchType}</span></td>
                          <td style={{ padding: "6px 8px" }}><span style={s.badge(intentColor[kw.intent] || C.textDim)}>{kw.intent}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderNegatives = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 600 }}>Negative Keywords</div>
      <div style={{ fontSize: 12, color: C.textSec }}>AI-identified keywords to exclude from your campaigns to prevent wasted spend.</div>
      {result?.negativeKeywords?.map((nk, i) => (
        <div key={i} style={{ ...s.cardSm, display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 16, color: C.red }}>−</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{nk.keyword}</div>
            <div style={{ fontSize: 11, color: C.textDim }}>{nk.reason}</div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderLongTail = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 600 }}>Long-Tail Opportunities</div>
      <div style={{ ...s.card, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>
            {["", "Keyword", "Volume", "CPC", "Opportunity"].map(h => <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: C.textDim, fontSize: 10, fontWeight: 600, textTransform: "uppercase" }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {result?.longTail?.map((lt, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.border}08` }}>
                <td style={{ padding: "8px 10px" }}><span onClick={() => toggleSaveKeyword(lt)} style={{ cursor: "pointer", fontSize: 14 }}>{savedKeywords.find(k => k.keyword === lt.keyword) ? "★" : "☆"}</span></td>
                <td style={{ padding: "8px 10px", fontWeight: 500 }}>{lt.keyword}</td>
                <td style={{ padding: "8px 10px", fontFamily: mono }}>{(lt.volume || 0).toLocaleString()}</td>
                <td style={{ padding: "8px 10px", fontFamily: mono }}>£{lt.cpc?.toFixed(2) || "—"}</td>
                <td style={{ padding: "8px 10px" }}><span style={s.badge(lt.opportunity === "High" ? C.green : C.amber)}>{lt.opportunity}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderQuestions = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 600 }}>Question Keywords</div>
      <div style={{ fontSize: 12, color: C.textSec, marginBottom: 4 }}>Questions your audience is searching for — great for RSA headlines and FAQ content.</div>
      {result?.questions?.map((q, i) => (
        <div key={i} style={{ ...s.cardSm, display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 16 }}>❓</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{q.question}</div>
            <div style={{ fontSize: 11, color: C.textDim }}>{(q.volume || 0).toLocaleString()} searches/mo</div>
          </div>
          <span style={s.badge(q.adOpportunity?.includes("RSA") ? C.green : q.adOpportunity?.includes("FAQ") ? C.blue : C.textDim)}>{q.adOpportunity}</span>
          <span onClick={() => toggleSaveKeyword({ keyword: q.question, volume: q.volume })} style={{ cursor: "pointer", fontSize: 14 }}>{savedKeywords.find(k => k.keyword === q.question) ? "★" : "☆"}</span>
        </div>
      ))}
    </div>
  );

  const renderSaved = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>Saved Keywords ({savedKeywords.length})</div>
        {savedKeywords.length > 0 && <button style={s.btnGhost} onClick={() => {
          let csv = "Keyword,Volume,CPC,Match Type\n";
          savedKeywords.forEach(k => { csv += `"${k.keyword}",${k.volume || ""},${k.cpc || ""},${k.matchType || ""}\n`; });
          const blob = new Blob([csv], { type: "text/csv" }); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "saved-keywords.csv"; a.click();
        }}>↗ Export CSV</button>}
      </div>
      {savedKeywords.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: C.textDim }}>Click ☆ on any keyword to save it here for export.</div>
      ) : (
        <div style={s.card}>
          {savedKeywords.map((kw, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
              <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{kw.keyword}</span>
              {kw.volume && <span style={{ fontFamily: mono, fontSize: 11, color: C.textDim }}>{kw.volume.toLocaleString()} vol</span>}
              {kw.cpc && <span style={{ fontFamily: mono, fontSize: 11, color: C.textDim }}>£{kw.cpc.toFixed(2)}</span>}
              <span onClick={() => toggleSaveKeyword(kw)} style={{ cursor: "pointer", color: C.red, fontSize: 12 }}>✕</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ display: "flex", height: "100%" }}>
      {sidebar}
      <div style={{ flex: 1, padding: 20, overflowY: "auto" }}>
        <div style={{ maxWidth: 900 }}>
          {tab === "research" && renderResearch()}
          {tab === "clusters" && renderClusters()}
          {tab === "negatives" && renderNegatives()}
          {tab === "longtail" && renderLongTail()}
          {tab === "questions" && renderQuestions()}
          {tab === "saved" && renderSaved()}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// SETTINGS MODULE
// ═══════════════════════════════════════════
function SettingsPanel({ user }) {
  const [tab, setTab] = useState("general");
  const [team, setTeam] = useState([
    { id: 1, name: "Sarah Mitchell", email: "sarah@agency.com", role: "admin", status: "active", lastSeen: "2 min ago" },
    { id: 2, name: "James Kirk", email: "james@agency.com", role: "manager", status: "active", lastSeen: "1 hour ago" },
    { id: 3, name: "Client: Apex Fitness", email: "mark@apexfitness.com", role: "viewer", status: "active", lastSeen: "3 days ago" },
  ]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("manager");
  const [saved, setSaved] = useState(false);

  const handleInvite = async () => {
    if (!inviteEmail) return;
    await api.request("/auth/invite", { method: "POST", body: JSON.stringify({ email: inviteEmail, role: inviteRole }) });
    setTeam(prev => [...prev, { id: Date.now(), name: inviteEmail.split("@")[0], email: inviteEmail, role: inviteRole, status: "invited", lastSeen: "Pending" }]);
    setInviteEmail("");
  };

  const TABS = [
    { id: "general", label: "General", icon: "⚙" },
    { id: "team", label: "Team & Access", icon: "👥" },
    { id: "connections", label: "API Connections", icon: "🔗" },
    { id: "notifications", label: "Notifications", icon: "🔔" },
  ];

  return (
    <div style={{ display: "flex", height: "100%" }}>
      <div style={{ width: 200, borderRight: `1px solid ${C.border}`, padding: "16px 8px", flexShrink: 0 }}>
        <div style={{ padding: "0 12px 12px", fontSize: 15, fontWeight: 700, color: C.textSec }}>⚙ Settings</div>
        {TABS.map(t => (
          <div key={t.id} style={s.sidebarItem(tab === t.id)} onClick={() => setTab(t.id)}>
            <span style={{ width: 18, textAlign: "center" }}>{t.icon}</span><span>{t.label}</span>
          </div>
        ))}
      </div>
      <div style={{ flex: 1, padding: 20, overflowY: "auto" }}>
        <div style={{ maxWidth: 700 }}>

          {tab === "general" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>General Settings</div>
              <div style={s.card}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Agency Profile</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 4 }}>Agency Name</label><input style={s.input} defaultValue="Your Agency" /></div>
                  <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 4 }}>Primary Email</label><input style={s.input} defaultValue={user?.email || ""} /></div>
                  <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 4 }}>Currency</label>
                    <select style={s.input}><option>GBP (£)</option><option>USD ($)</option><option>EUR (€)</option></select>
                  </div>
                  <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 4 }}>Timezone</label>
                    <select style={s.input}><option>Europe/London (GMT)</option><option>America/New_York (EST)</option><option>Europe/Berlin (CET)</option></select>
                  </div>
                </div>
                <button style={{ ...s.btn, marginTop: 16 }} onClick={() => { setSaved(true); setTimeout(() => setSaved(false), 2000); }}>{saved ? "✓ Saved" : "Save Changes"}</button>
              </div>
              <div style={s.card}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Data & Privacy</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13, color: C.textSec }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8 }}><input type="checkbox" defaultChecked style={{ accentColor: C.accent }} /> Enable audit logging for all actions</label>
                  <label style={{ display: "flex", alignItems: "center", gap: 8 }}><input type="checkbox" defaultChecked style={{ accentColor: C.accent }} /> Auto-generate weekly reports</label>
                  <label style={{ display: "flex", alignItems: "center", gap: 8 }}><input type="checkbox" style={{ accentColor: C.accent }} /> Allow clients to view search terms</label>
                </div>
              </div>
            </div>
          )}

          {tab === "team" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>Team & Access Control</div>
              <div style={s.card}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Invite Team Member</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input style={{ ...s.input, flex: 1 }} placeholder="email@agency.com" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} />
                  <select style={{ ...s.input, width: 120 }} value={inviteRole} onChange={e => setInviteRole(e.target.value)}>
                    <option value="admin">Admin</option><option value="manager">Manager</option><option value="viewer">Viewer</option>
                  </select>
                  <button style={s.btn} onClick={handleInvite}>Send Invite</button>
                </div>
              </div>
              <div style={s.card}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Team Members ({team.length})</div>
                {team.map(m => (
                  <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                    <div style={{ width: 36, height: 36, borderRadius: 18, background: `${C.accent}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: C.accent }}>
                      {m.name.charAt(0)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{m.name}</div>
                      <div style={{ fontSize: 11, color: C.textDim }}>{m.email}</div>
                    </div>
                    <span style={s.badge(m.role === "admin" ? C.accent : m.role === "manager" ? C.blue : C.textDim)}>{m.role}</span>
                    <span style={s.badge(m.status === "active" ? C.green : C.amber)}>{m.status}</span>
                    <span style={{ fontSize: 11, color: C.textDim }}>{m.lastSeen}</span>
                  </div>
                ))}
              </div>
              <div style={s.card}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Role Permissions</div>
                <div style={{ fontSize: 12, color: C.textSec, lineHeight: 1.7 }}>
                  <strong style={{ color: C.accent }}>Admin</strong> — Full access. Manage team, apply improvements, change budgets, modify feed rules, export data.<br/>
                  <strong style={{ color: C.blue }}>Manager</strong> — Read/write access. Apply improvements, run feed rules, generate reports. Cannot manage team.<br/>
                  <strong style={{ color: C.textDim }}>Viewer</strong> — Read-only. View dashboards and reports. Ideal for client access portals.
                </div>
              </div>
            </div>
          )}

          {tab === "connections" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>API Connections</div>
              {[
                { name: "Google Ads API", desc: "PPC campaign management and reporting", status: api.isConnected, icon: "📊", fields: ["Developer Token", "MCC Customer ID", "OAuth Client ID"] },
                { name: "WooCommerce", desc: "Product feed sync for FeedForge", status: false, icon: "🛒", fields: ["Store URL", "Consumer Key", "Consumer Secret"] },
                { name: "Google Merchant Center", desc: "Shopping feed submission", status: false, icon: "🏪", fields: ["Merchant ID", "Feed URL"] },
              ].map((conn, i) => (
                <div key={i} style={{ ...s.card, borderLeft: `3px solid ${conn.status ? C.green : C.textDim}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                    <span style={{ fontSize: 24 }}>{conn.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{conn.name}</div>
                      <div style={{ fontSize: 12, color: C.textDim }}>{conn.desc}</div>
                    </div>
                    <span style={s.badge(conn.status ? C.green : C.textDim)}>{conn.status ? "Connected" : "Not connected"}</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {conn.fields.map(f => (
                      <div key={f}>
                        <label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 4 }}>{f}</label>
                        <input style={s.input} type="password" placeholder="••••••••" />
                      </div>
                    ))}
                  </div>
                  <button style={{ ...s.btn, marginTop: 12 }}>{conn.status ? "Update" : "Connect"}</button>
                </div>
              ))}
            </div>
          )}

          {tab === "notifications" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>Notification Preferences</div>
              <div style={s.card}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Email Alerts</div>
                {[
                  { label: "Critical errors (tracking failures, disapprovals)", default: true },
                  { label: "Budget overspend warnings", default: true },
                  { label: "Weekly performance summary", default: true },
                  { label: "New improvement recommendations", default: false },
                  { label: "Feed quality drops below threshold", default: true },
                  { label: "Team member activity", default: false },
                ].map((n, i) => (
                  <label key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", fontSize: 13, color: C.textSec }}>
                    <input type="checkbox" defaultChecked={n.default} style={{ accentColor: C.accent }} /> {n.label}
                  </label>
                ))}
              </div>
              <div style={s.card}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Alert Thresholds</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 4 }}>Budget overspend threshold (%)</label><input style={{ ...s.input, width: 100 }} type="number" defaultValue="110" /></div>
                  <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 4 }}>Feed score alert threshold</label><input style={{ ...s.input, width: 100 }} type="number" defaultValue="70" /></div>
                  <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 4 }}>Wasted spend alert (£/month)</label><input style={{ ...s.input, width: 100 }} type="number" defaultValue="200" /></div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// ═══════════════════════════════════════════
// ROLE-BASED ACCESS CONTROL
// ═══════════════════════════════════════════
// Roles: admin (agency owner), manager (account manager), viewer (client)
// Per-account: clients only see their assigned account data
// ═══════════════════════════════════════════
// ═══════════════════════════════════════════
// PRO TOOLS — 14 Advanced Features (Inline)
// ═══════════════════════════════════════════
function ProTools() {
  const [view, setView] = useState("rules");

  const NAV = [
    { id: "rules", label: "Auto Rules", icon: "⚡" }, { id: "changes", label: "Change History", icon: "↩" },
    { id: "portal", label: "Client Portal", icon: "🌐" }, { id: "anomalies", label: "Anomalies", icon: "🔔" },
    { id: "competitors", label: "Competitors", icon: "👁" }, { id: "landing", label: "Landing Pages", icon: "📄" },
    { id: "abtests", label: "A/B Testing", icon: "🧪" }, { id: "forecast", label: "Budget Forecast", icon: "📈" },
    { id: "negatives", label: "Negative Library", icon: "−" }, { id: "adcopy", label: "AI Ad Copy", icon: "✍️" },
    { id: "attribution", label: "Attribution", icon: "🔀" }, { id: "proposals", label: "Proposals", icon: "📋" },
    { id: "scripts", label: "Scripts Library", icon: "📜" }, { id: "billing", label: "Billing", icon: "💰" },
  ];

  // ── Demo Data ──
  const RULES = [
    { id: 1, name: "Pause high CPA keywords", enabled: true, entity: "keyword", condition: "CPA > £25 for 7d", action: "Pause", schedule: "daily", lastRun: "2 hours ago", triggered: 3, color: C.red },
    { id: 2, name: "Increase budget for high ROAS", enabled: true, entity: "campaign", condition: "ROAS > 5 for 14d", action: "Increase Budget 10%", schedule: "weekly", lastRun: "3 days ago", triggered: 1, color: C.green },
    { id: 3, name: "Alert on zero conversions", enabled: true, entity: "campaign", condition: "Conv = 0 for 3d, Spend > £50", action: "Alert", schedule: "daily", lastRun: "2 hours ago", triggered: 0, color: C.blue },
    { id: 4, name: "Reduce bid on low QS", enabled: false, entity: "keyword", condition: "QS < 4 for 30d", action: "Decrease Bid 15%", schedule: "weekly", lastRun: "1 week ago", triggered: 5, color: C.amber },
    { id: 5, name: "Pause ads with low CTR", enabled: true, entity: "ad", condition: "CTR < 1.5% for 14d, Impr > 500", action: "Pause", schedule: "daily", lastRun: "2 hours ago", triggered: 2, color: C.red },
  ];
  const [rulesState, setRulesState] = useState(RULES);
  const RULE_LOG = [
    { time: "Today 09:00", rule: "Pause high CPA keywords", action: "Paused 'cheap gym equipment' in Apex Fitness (CPA: £32.40)" },
    { time: "Today 09:00", rule: "Pause high CPA keywords", action: "Paused 'fitness classes free' in Bloom Botanicals (CPA: £41.20)" },
    { time: "Today 09:00", rule: "Pause ads with low CTR", action: "Paused RSA #4 in EchoStream Audio (CTR: 0.8%)" },
    { time: "Mon 09:00", rule: "Increase budget for high ROAS", action: "ClearView Optics budget £200→£220/day (ROAS: 7.1x)" },
  ];

  const CHANGES = [
    { id: 1, time: "Today 14:32", user: "James K.", account: "Apex Fitness", entity: "bid", action: "Bid increased", detail: "'personal trainer near me': £1.80 → £2.10", rolledBack: false },
    { id: 2, time: "Today 12:15", user: "Sarah M.", account: "Bloom Botanicals", entity: "ad", action: "Ad paused", detail: "RSA variant #3 in 'Spring Collection'", rolledBack: false },
    { id: 3, time: "Today 09:00", user: "Auto Rule", account: "Apex Fitness", entity: "keyword", action: "Keyword paused", detail: "'cheap gym equipment' (CPA: £32.40 > £25)", rolledBack: false },
    { id: 4, time: "Yesterday", user: "James K.", account: "ClearView Optics", entity: "budget", action: "Budget increased", detail: "'Eye Tests': £180/day → £200/day", rolledBack: false },
    { id: 5, time: "2 days ago", user: "James K.", account: "Forge & Anvil", entity: "bid", action: "Bid decreased", detail: "'bespoke furniture cheap': £2.40 → £1.90", rolledBack: true },
  ];
  const [changesState, setChangesState] = useState(CHANGES);

  const ANOMALIES = [
    { id: 1, time: "32 min ago", account: "Apex Fitness", metric: "CPC", severity: "high", current: "£3.42", average: "£1.87", change: "+83%", detail: "CPC spike on 'personal trainer' — likely new competitor" },
    { id: 2, time: "2 hours ago", account: "DriftWood Homes", metric: "Conv", severity: "critical", current: "0", average: "4.2/day", change: "-100%", detail: "Zero conversions since 11am — tracking issue likely" },
    { id: 3, time: "4 hours ago", account: "Bloom Botanicals", metric: "Impr", severity: "medium", current: "1,240", average: "3,800", change: "-67%", detail: "Impression drop — check budget or bid competitiveness" },
    { id: 4, time: "Yesterday", account: "ClearView Optics", metric: "CTR", severity: "low", current: "7.2%", average: "5.6%", change: "+29%", detail: "CTR spike — new RSA variant performing exceptionally" },
  ];

  const COMPETITORS = [
    { name: "PureGym", impShare: 28, overlap: 72, position: 1.8, outranking: 34, trend: "up", isNew: false },
    { name: "David Lloyd", impShare: 22, overlap: 58, position: 2.1, outranking: 41, trend: "stable", isNew: false },
    { name: "The Gym Group", impShare: 18, overlap: 65, position: 2.4, outranking: 52, trend: "down", isNew: false },
    { name: "FitSpace Studios", impShare: 8, overlap: 32, position: 3.2, outranking: 78, trend: "up", isNew: true },
  ];

  const FORECASTS = [
    { account: "Apex Fitness", budget: 15000, spent: 9200, projected: 14300, daysLeft: 10, status: "under", dailyAdj: "£580" },
    { account: "Bloom Botanicals", budget: 10000, spent: 7800, projected: 12133, daysLeft: 10, status: "over", dailyAdj: "£220" },
    { account: "ClearView Optics", budget: 28000, spent: 17800, projected: 27689, daysLeft: 10, status: "on-track", dailyAdj: "£1,020" },
    { account: "DriftWood Homes", budget: 16000, spent: 14200, projected: 22089, daysLeft: 10, status: "over", dailyAdj: "£180" },
  ];

  const NEGS = [
    { id: 1, name: "Generic / Freebie Terms", keywords: ["free", "cheap", "diy", "how to", "tutorial", "reddit", "youtube", "wikipedia"], accounts: "3 accounts", color: C.red },
    { id: 2, name: "Job Seekers", keywords: ["jobs", "salary", "hiring", "vacancy", "career", "cv", "glassdoor"], accounts: "All accounts", color: C.purple },
    { id: 3, name: "Competitor Brands", keywords: ["puregym", "david lloyd", "the gym group", "fitness first"], accounts: "Apex Fitness", color: C.orange },
  ];

  const SCRIPTS = [
    { name: "Broken URL Checker", desc: "Scans all ad URLs for 404 errors and redirects", cat: "Maintenance", rating: 4.8 },
    { name: "Quality Score Tracker", desc: "Records daily QS to a Google Sheet for trend analysis", cat: "Reporting", rating: 4.7 },
    { name: "N-Gram Analysis", desc: "Analyses search terms by word combinations to find patterns", cat: "Optimisation", rating: 4.9 },
    { name: "Budget Pacing Alert", desc: "Emails when campaigns are >110% or <80% of pacing", cat: "Monitoring", rating: 4.8 },
    { name: "RSA Asset Performance", desc: "Exports RSA headline/description ratings to a Sheet", cat: "Reporting", rating: 4.5 },
    { name: "Auto-Label by Performance", desc: "Labels keywords as Hero, Villain, or Potential", cat: "Optimisation", rating: 4.6 },
  ];

  const BILLING = [
    { name: "Apex Fitness Co.", spend: 12450, fee: 2200, type: "Fixed", margin: 17.7, status: "paid" },
    { name: "Bloom Botanicals", spend: 8320, fee: 1250, type: "% Spend", margin: 15.0, status: "paid" },
    { name: "ClearView Optics", spend: 24100, fee: 3600, type: "% Spend", margin: 14.9, status: "pending" },
    { name: "Forge & Anvil", spend: 19500, fee: 2900, type: "% Spend", margin: 14.9, status: "overdue" },
  ];

  const sevColors = { critical: C.red, high: "#f97316", medium: C.amber, low: C.blue };
  const entColors = { bid: C.amber, ad: C.purple, keyword: C.accent, budget: C.cyan };

  const renderContent = () => {
    switch (view) {
      case "rules": return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div><div style={{ fontSize: 16, fontWeight: 700 }}>Automated Rules</div><div style={{ fontSize: 12, color: C.textDim }}>Conditions to auto-manage bids, budgets, and status across accounts</div></div>
            <button style={s.btn}>+ New Rule</button>
          </div>
          {rulesState.map(rule => (
            <div key={rule.id} style={{ ...s.card, borderLeft: `3px solid ${rule.enabled ? rule.color : C.textDim}`, opacity: rule.enabled ? 1 : 0.5 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input type="checkbox" checked={rule.enabled} onChange={() => setRulesState(p => p.map(r => r.id === rule.id ? { ...r, enabled: !r.enabled } : r))} style={{ accentColor: C.accent }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{rule.name}</div>
                  <div style={{ fontSize: 11, color: C.textDim }}>IF <span style={{ color: C.accent }}>{rule.entity}</span> {rule.condition} → <span style={{ color: rule.color }}>{rule.action}</span></div>
                </div>
                <span style={s.badge(C.blue)}>{rule.schedule}</span>
                <div style={{ textAlign: "right", fontSize: 11, color: C.textDim }}><div>Last: {rule.lastRun}</div><div style={{ color: rule.triggered > 0 ? C.amber : C.green }}>{rule.triggered} triggered</div></div>
              </div>
            </div>
          ))}
          <div style={s.card}><div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Recent Executions</div>
            {RULE_LOG.map((e, i) => (
              <div key={i} style={{ display: "flex", gap: 8, padding: "6px 0", borderBottom: `1px solid ${C.border}`, fontSize: 12 }}>
                <span style={{ color: C.textDim, width: 80, flexShrink: 0 }}>{e.time}</span>
                <span style={{ color: C.accent, width: 170, flexShrink: 0 }}>{e.rule}</span>
                <span style={{ color: C.textSec }}>{e.action}</span>
              </div>
            ))}
          </div>
        </div>
      );
      case "changes": return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Change History & Rollback</div>
          {changesState.map(ch => (
            <div key={ch.id} style={{ ...s.card, borderLeft: `3px solid ${entColors[ch.entity] || C.accent}`, opacity: ch.rolledBack ? 0.5 : 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}><span style={s.badge(entColors[ch.entity] || C.accent)}>{ch.entity}</span><span style={{ fontSize: 13, fontWeight: 600 }}>{ch.action}</span><span style={{ fontSize: 11, color: C.textDim }}>· {ch.account}</span>{ch.rolledBack && <span style={s.badge(C.amber)}>Rolled back</span>}</div>
                  <div style={{ fontSize: 12, color: C.textSec }}>{ch.detail}</div>
                  <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>{ch.user} · {ch.time}</div>
                </div>
                {!ch.rolledBack && <button style={{ ...s.btnGhost, padding: "4px 10px", fontSize: 11, color: C.amber, borderColor: `${C.amber}40` }} onClick={() => setChangesState(p => p.map(c => c.id === ch.id ? { ...c, rolledBack: true } : c))}>↩ Rollback</button>}
              </div>
            </div>
          ))}
        </div>
      );
      case "portal": return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Client Reporting Portal</div>
          <div style={{ fontSize: 12, color: C.textDim }}>White-labelled dashboards for each client with shareable URLs.</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
            {[{ name: "Apex Fitness Co.", icon: "🏋️", slug: "apex-fitness", live: true, views: 23 }, { name: "Bloom Botanicals", icon: "🌸", slug: "bloom-botanicals", live: true, views: 8 }, { name: "ClearView Optics", icon: "👁", slug: "clearview", live: true, views: 15 }, { name: "DriftWood Homes", icon: "🏠", slug: "driftwood", live: false, views: 0 }].map((cl, i) => (
              <div key={i} style={s.card}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}><span style={{ fontSize: 24 }}>{cl.icon}</span><div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 600 }}>{cl.name}</div></div><span style={s.badge(cl.live ? C.green : C.textDim)}>{cl.live ? "Live" : "Off"}</span></div>
                <div style={{ background: C.surface, borderRadius: 6, padding: "5px 8px", fontFamily: mono, fontSize: 11, color: C.textDim, marginBottom: 6 }}>portal.agency.com/{cl.slug}</div>
                <div style={{ fontSize: 11, color: C.textDim }}>{cl.views} views this month</div>
              </div>
            ))}
          </div>
        </div>
      );
      case "anomalies": return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Anomaly Detection</div>
          <div style={{ display: "flex", gap: 12 }}><KpiCard label="Active" value={ANOMALIES.length} color={C.amber} /><KpiCard label="Critical" value={ANOMALIES.filter(a => a.severity === "critical").length} color={C.red} /></div>
          {ANOMALIES.map(a => (
            <div key={a.id} style={{ ...s.card, borderLeft: `3px solid ${sevColors[a.severity]}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}><span style={s.badge(sevColors[a.severity])}>{a.severity}</span><span style={{ fontSize: 13, fontWeight: 600 }}>{a.account} — {a.metric}</span><span style={{ marginLeft: "auto", fontSize: 11, color: C.textDim }}>{a.time}</span></div>
              <div style={{ display: "flex", gap: 16, fontSize: 12, marginBottom: 4 }}><span>Current: <strong style={{ fontFamily: mono }}>{a.current}</strong></span><span>Avg: <strong style={{ fontFamily: mono }}>{a.average}</strong></span><span style={{ color: C.red, fontWeight: 700, fontFamily: mono }}>{a.change}</span></div>
              <div style={{ fontSize: 12, color: C.textSec }}>{a.detail}</div>
            </div>
          ))}
        </div>
      );
      case "competitors": return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Competitor Monitoring</div>
          <div style={{ ...s.card, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>{["Competitor", "Imp. Share", "Overlap", "Avg Pos", "Outranking", "Trend"].map(h => <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: C.textDim, fontWeight: 600, fontSize: 10, textTransform: "uppercase" }}>{h}</th>)}</tr></thead>
              <tbody>{COMPETITORS.map((c, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.border}08` }}>
                  <td style={{ padding: "8px 10px", fontWeight: 600 }}>{c.name} {c.isNew && <span style={s.badge(C.pink)}>NEW</span>}</td>
                  <td style={{ padding: "8px 10px" }}><div style={{ display: "flex", alignItems: "center", gap: 6 }}><MiniBar value={c.impShare} max={30} color={C.accent} /><span style={{ fontFamily: mono, fontSize: 12 }}>{c.impShare}%</span></div></td>
                  <td style={{ padding: "8px 10px", fontFamily: mono }}>{c.overlap}%</td>
                  <td style={{ padding: "8px 10px", fontFamily: mono }}>{c.position}</td>
                  <td style={{ padding: "8px 10px", fontFamily: mono, color: c.outranking > 50 ? C.green : C.red }}>{c.outranking}%</td>
                  <td style={{ padding: "8px 10px", color: c.trend === "up" ? C.red : c.trend === "down" ? C.green : C.textDim }}>{c.trend === "up" ? "▲ Gaining" : c.trend === "down" ? "▼ Losing" : "— Stable"}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      );
      case "landing": return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Landing Page Analyser</div>
          <div style={s.card}><div style={{ display: "flex", gap: 8 }}><input style={{ ...s.input, flex: 1 }} placeholder="https://client-website.com/landing-page" /><button style={s.btn}>🔍 Analyse</button></div>
            <div style={{ fontSize: 12, color: C.textDim, marginTop: 8 }}>Enter a URL to get AI-powered analysis of speed, mobile-friendliness, CTAs, keyword relevance, and trust signals. Requires Anthropic API key.</div>
          </div>
          <div style={{ display: "flex", gap: 12 }}><KpiCard label="Overall Score" value="78/100" color={C.amber} /><KpiCard label="Speed" value="82/100" color={C.green} /><KpiCard label="Mobile" value="71/100" color={C.amber} /><KpiCard label="CTA" value="65/100" color={C.red} /></div>
          <div style={s.card}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Sample Recommendations</div>
            {[{ p: "High", t: "Add clear CTA above the fold", d: "Primary action button isn't visible without scrolling" }, { p: "High", t: "Reduce page load time", d: "3.2s load — aim for under 2s for PPC landing pages" }, { p: "Medium", t: "Add trust signals", d: "No reviews, testimonials, or certifications visible" }].map((r, i) => (
              <div key={i} style={{ display: "flex", gap: 8, padding: "6px 0", borderBottom: `1px solid ${C.border}` }}><span style={s.badge(r.p === "High" ? C.red : C.amber)}>{r.p}</span><div><div style={{ fontSize: 12, fontWeight: 600 }}>{r.t}</div><div style={{ fontSize: 11, color: C.textDim }}>{r.d}</div></div></div>
            ))}
          </div>
        </div>
      );
      case "abtests": return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Ad Copy A/B Testing</div>
          {[{ name: "Price vs Quality — Apex Fitness", status: "running", days: 16, a: { label: "Price Focus", ctr: 6.0, conv: 28, cpa: 16.20 }, b: { label: "Quality Focus", ctr: 7.0, conv: 34, cpa: 13.80 }, conf: 72 },
            { name: "CTA Test — ClearView Optics", status: "winner", days: 28, a: { label: "Book Now", ctr: 7.0, conv: 62, cpa: 11.20 }, b: { label: "Learn More", ctr: 6.0, conv: 44, cpa: 14.80 }, conf: 95 }].map((t, ti) => (
            <div key={ti} style={s.card}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}><span style={{ fontSize: 13, fontWeight: 700, flex: 1 }}>{t.name}</span><span style={s.badge(t.status === "running" ? C.blue : C.green)}>{t.status === "running" ? `Running · Day ${t.days}` : "Winner found"}</span><span style={{ fontSize: 11, color: C.textDim }}>Conf: {t.conf}%</span></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {[t.a, t.b].map((v, vi) => {
                  const isWinner = t.status === "winner" && v.conv > (vi === 0 ? t.b.conv : t.a.conv);
                  return <div key={vi} style={{ background: C.surface, borderRadius: 8, padding: 12, border: `1px solid ${isWinner ? C.green : C.border}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}><span style={{ fontSize: 12, fontWeight: 700 }}>{v.label}</span>{isWinner && <span style={s.badge(C.green)}>WINNER</span>}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, fontSize: 12 }}><div><span style={{ color: C.textDim }}>CTR </span><span style={{ fontFamily: mono, fontWeight: 600 }}>{v.ctr}%</span></div><div><span style={{ color: C.textDim }}>Conv </span><span style={{ fontFamily: mono, fontWeight: 600 }}>{v.conv}</span></div><div><span style={{ color: C.textDim }}>CPA </span><span style={{ fontFamily: mono, fontWeight: 600, color: isWinner ? C.green : C.text }}>£{v.cpa}</span></div></div>
                  </div>;
                })}
              </div>
            </div>
          ))}
        </div>
      );
      case "forecast": return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Budget Forecasting</div>
          <div style={{ display: "flex", gap: 12 }}><KpiCard label="Portfolio Budget" value="£69,000" /><KpiCard label="Projected" value="£76,211" color={C.amber} /><KpiCard label="Overspending" value="2" color={C.red} /></div>
          {FORECASTS.map((f, i) => {
            const pct = (f.spent / f.budget) * 100;
            const sc = f.status === "over" ? C.red : f.status === "under" ? C.amber : C.green;
            return <div key={i} style={{ ...s.card, borderLeft: `3px solid ${sc}` }}>
              <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}><span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{f.account}</span><span style={s.badge(sc)}>{f.status === "over" ? "Overspend risk" : f.status === "under" ? "Underspend" : "On track"}</span></div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.textDim, marginBottom: 3 }}><span>£{f.spent.toLocaleString()} spent</span><span>£{f.budget.toLocaleString()} budget</span></div>
              <MiniBar value={f.spent} max={f.budget} color={sc} height={8} />
              <div style={{ display: "flex", gap: 16, fontSize: 12, color: C.textSec, marginTop: 6 }}><span>Projected: <strong style={{ color: sc, fontFamily: mono }}>£{f.projected.toLocaleString()}</strong></span><span>Adjust to {f.dailyAdj}/day</span><span>{f.daysLeft} days left</span></div>
            </div>;
          })}
        </div>
      );
      case "negatives": return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}><div><div style={{ fontSize: 16, fontWeight: 700 }}>Shared Negative Keyword Library</div><div style={{ fontSize: 12, color: C.textDim }}>Master lists applied across accounts</div></div><button style={s.btn}>+ New List</button></div>
          {NEGS.map(list => (
            <div key={list.id} style={{ ...s.card, borderLeft: `3px solid ${list.color}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}><span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{list.name}</span><span style={{ fontSize: 11, color: C.textDim }}>{list.keywords.length} keywords · {list.accounts}</span></div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>{list.keywords.map((kw, i) => <span key={i} style={{ fontSize: 11, padding: "2px 7px", borderRadius: 4, background: `${list.color}12`, color: list.color, border: `1px solid ${list.color}25` }}>−{kw}</span>)}</div>
            </div>
          ))}
        </div>
      );
      case "adcopy": return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>AI Ad Copy Generator</div>
          <div style={s.card}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 3 }}>Business</label><input style={s.input} placeholder="e.g. Apex Fitness" /></div>
              <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 3 }}>Product</label><input style={s.input} placeholder="e.g. Personal Training" /></div>
            </div>
            <button style={{ ...s.btn, width: "100%", background: `linear-gradient(135deg, ${C.purple}, ${C.pink})` }}>✍️ Generate Ad Variants</button>
            <div style={{ fontSize: 11, color: C.textDim, marginTop: 6 }}>Generates 3 RSA variants with 15 headlines + 4 descriptions each. Requires Anthropic API key.</div>
          </div>
        </div>
      );
      case "attribution": return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Multi-Channel Attribution</div>
          <div style={{ ...s.card, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>{["Channel", "Attribution %", "Conversions", "Revenue", "ROAS"].map(h => <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: C.textDim, fontWeight: 600, fontSize: 10, textTransform: "uppercase" }}>{h}</th>)}</tr></thead>
              <tbody>{[{ ch: "Google Search", pct: 37, conv: 890, rev: "£156k", roas: "3.0x", c: C.accent }, { ch: "Google Shopping", pct: 21, conv: 420, rev: "£84k", roas: "3.0x", c: C.cyan }, { ch: "Organic", pct: 17, conv: 340, rev: "£51k", roas: "—", c: C.green }, { ch: "Direct", pct: 15, conv: 280, rev: "£42k", roas: "—", c: C.amber }, { ch: "Meta", pct: 10, conv: 190, rev: "£28.5k", roas: "2.4x", c: C.pink }].map((r, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.border}08` }}>
                  <td style={{ padding: "8px 10px", fontWeight: 500 }}>{r.ch}</td>
                  <td style={{ padding: "8px 10px", width: 160 }}><div style={{ display: "flex", alignItems: "center", gap: 6 }}><MiniBar value={r.pct} max={40} color={r.c} /><span style={{ fontFamily: mono, fontSize: 12 }}>{r.pct}%</span></div></td>
                  <td style={{ padding: "8px 10px", fontFamily: mono }}>{r.conv}</td>
                  <td style={{ padding: "8px 10px", fontFamily: mono }}>{r.rev}</td>
                  <td style={{ padding: "8px 10px", fontFamily: mono, color: r.roas !== "—" ? C.green : C.textDim }}>{r.roas}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      );
      case "proposals": return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Proposal Generator</div>
          <div style={s.card}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 3 }}>Prospect Name</label><input style={s.input} placeholder="e.g. FreshBite Restaurants" /></div>
              <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 3 }}>Website</label><input style={s.input} placeholder="https://freshbite.co.uk" /></div>
              <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 3 }}>Industry</label><input style={s.input} placeholder="e.g. Restaurant chain" /></div>
              <div><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 3 }}>Monthly Ad Spend</label><input style={s.input} placeholder="e.g. £5,000" /></div>
            </div>
            <button style={{ ...s.btn, width: "100%", background: `linear-gradient(135deg, ${C.teal}, ${C.blue})` }}>📋 Generate Client Proposal</button>
            <div style={{ fontSize: 11, color: C.textDim, marginTop: 6 }}>Generates executive summary, audit, strategy, projections, and fee structure. Requires Anthropic API key.</div>
          </div>
        </div>
      );
      case "scripts": return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Google Ads Scripts Library</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
            {SCRIPTS.map((sc, i) => (
              <div key={i} style={s.card}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}><span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{sc.name}</span><span style={s.badge(C.blue)}>{sc.cat}</span></div>
                <div style={{ fontSize: 12, color: C.textSec, marginBottom: 8 }}>{sc.desc}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={{ fontSize: 11, color: C.amber }}>{"★".repeat(Math.floor(sc.rating))} {sc.rating}</span><button style={{ ...s.btn, padding: "3px 10px", fontSize: 11, marginLeft: "auto" }}>Install</button></div>
              </div>
            ))}
          </div>
        </div>
      );
      case "billing": return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Billing & Margin Tracker</div>
          <div style={{ display: "flex", gap: 12 }}><KpiCard label="Total Spend" value="£64.4k" /><KpiCard label="Fees" value="£10.0k" color={C.green} /><KpiCard label="Avg Margin" value="15.5%" color={C.blue} /><KpiCard label="Overdue" value="1" color={C.red} /></div>
          <div style={{ ...s.card, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>{["Client", "Ad Spend", "Mgmt Fee", "Type", "Margin", "Status"].map(h => <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: C.textDim, fontWeight: 600, fontSize: 10, textTransform: "uppercase" }}>{h}</th>)}</tr></thead>
              <tbody>{BILLING.map((b, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.border}08` }}>
                  <td style={{ padding: "8px 10px", fontWeight: 500 }}>{b.name}</td>
                  <td style={{ padding: "8px 10px", fontFamily: mono }}>£{b.spend.toLocaleString()}</td>
                  <td style={{ padding: "8px 10px", fontFamily: mono, color: C.green }}>£{b.fee.toLocaleString()}</td>
                  <td style={{ padding: "8px 10px" }}><span style={s.badge(b.type === "Fixed" ? C.blue : C.purple)}>{b.type}</span></td>
                  <td style={{ padding: "8px 10px", fontFamily: mono }}>{b.margin}%</td>
                  <td style={{ padding: "8px 10px" }}><span style={s.badge(b.status === "paid" ? C.green : b.status === "overdue" ? C.red : C.amber)}>{b.status}</span></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      );
      default: return null;
    }
  };

  return (
    <div style={{ display: "flex", height: "100%" }}>
      <div style={{ width: 190, borderRight: `1px solid ${C.border}`, padding: "12px 8px", overflowY: "auto", flexShrink: 0 }}>
        <div style={{ padding: "0 12px 10px", fontSize: 15, fontWeight: 700, background: `linear-gradient(135deg, ${C.accent}, ${C.pink})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>🚀 Pro Tools</div>
        {NAV.map(n => (
          <div key={n.id} style={s.sidebarItem(view === n.id)} onClick={() => setView(n.id)}>
            <span style={{ width: 16, textAlign: "center", fontSize: 11 }}>{n.icon}</span>
            <span style={{ fontSize: 12 }}>{n.label}</span>
          </div>
        ))}
      </div>
      <div style={{ flex: 1, padding: 20, overflowY: "auto" }}>
        <div style={{ maxWidth: 900 }}>{renderContent()}</div>
      </div>
    </div>
  );
}

const ROLE_CONFIG = {
  admin: {
    label: "Admin",
    color: "#ef4444",
    modules: ["ppc", "feedforge", "campaignai", "keywordai", "pro", "activity", "settings"],
    canApplyImprovements: true,
    canEditBids: true,
    canPauseCampaigns: true,
    canManageTeam: true,
    canViewAllAccounts: true,
    canExport: true,
    canManageRules: true,
    canViewBilling: true,
    canGenerateProposals: true,
  },
  manager: {
    label: "Manager",
    color: "#6366f1",
    modules: ["ppc", "feedforge", "campaignai", "keywordai", "pro", "activity"],
    canApplyImprovements: true,
    canEditBids: true,
    canPauseCampaigns: true,
    canManageTeam: false,
    canViewAllAccounts: true,
    canExport: true,
    canManageRules: true,
    canViewBilling: false,
    canGenerateProposals: true,
  },
  viewer: {
    label: "Client",
    color: "#10b981",
    modules: ["ppc"],
    ppcSections: ["overview", "performance", "segments", "budgets", "scorecard", "reports"],
    canApplyImprovements: false,
    canEditBids: false,
    canPauseCampaigns: false,
    canManageTeam: false,
    canViewAllAccounts: false,
    canExport: false,
    canManageRules: false,
    canViewBilling: false,
    canGenerateProposals: false,
  },
};

function RoleBadge({ role }) {
  const cfg = ROLE_CONFIG[role] || ROLE_CONFIG.viewer;
  return <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: `${cfg.color}18`, color: cfg.color, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>{cfg.label}</span>;
}

// ═══════════════════════════════════════════
// CLIENT DASHBOARD — Restricted View for Viewers
// ═══════════════════════════════════════════
function ClientDashboard({ user }) {
  const [section, setSection] = useState("overview");
  const clientSections = ROLE_CONFIG.viewer.ppcSections;

  const sectionLabels = {
    overview: "Overview", performance: "Performance", segments: "Segments",
    budgets: "Budgets", scorecard: "Scorecard", reports: "Reports",
  };

  // Demo: client sees only their account data
  const accountName = user.accountName || "Your Account";
  const kpis = [
    { label: "Spend This Month", value: "£12,450", sub: "+8.2% vs last month", trend: "up" },
    { label: "Conversions", value: "342", sub: "+12.4% vs last month", trend: "up" },
    { label: "ROAS", value: "5.2x", sub: "+0.4x improvement", trend: "up" },
    { label: "Cost Per Conversion", value: "£36.40", sub: "-6.1% reduction", trend: "down" },
  ];

  return (
    <div style={{ display: "flex", height: "100vh", background: C.bg, fontFamily: font, color: C.text }}>
      {/* Sidebar */}
      <div style={{ width: 220, borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column" }}>
        <div style={{ padding: 20, borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 18, fontWeight: 800, background: `linear-gradient(135deg, ${C.accent}, ${C.cyan})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Agency Hub</div>
          <div style={{ fontSize: 11, color: C.textDim, marginTop: 4 }}>{accountName}</div>
        </div>
        <div style={{ padding: "12px 8px", flex: 1 }}>
          {clientSections.map(sec => (
            <div key={sec} style={{
              padding: "10px 16px", borderRadius: 8, cursor: "pointer", fontSize: 13,
              fontWeight: section === sec ? 600 : 400, color: section === sec ? C.text : C.textSec,
              background: section === sec ? `${C.accent}15` : "transparent",
              borderLeft: section === sec ? `3px solid ${C.accent}` : "3px solid transparent",
              marginBottom: 2, transition: "all 0.15s",
            }} onClick={() => setSection(sec)}>
              {sectionLabels[sec]}
            </div>
          ))}
        </div>
        <div style={{ padding: 16, borderTop: `1px solid ${C.border}`, fontSize: 11, color: C.textDim }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <div style={{ width: 24, height: 24, borderRadius: "50%", background: C.green, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff" }}>
              {(user.name || "C").charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ color: C.text, fontWeight: 500 }}>{user.name}</div>
              <RoleBadge role="viewer" />
            </div>
          </div>
          <span style={{ cursor: "pointer", color: C.accent, fontSize: 11 }}>Sign out</span>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, padding: 24, overflowY: "auto" }}>
        <div style={{ maxWidth: 900 }}>
          {section === "overview" && (
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Welcome back, {user.name}</div>
              <div style={{ fontSize: 13, color: C.textDim, marginBottom: 20 }}>Here's how your campaigns are performing this month.</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
                {kpis.map((k, i) => (
                  <div key={i} style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, padding: 16 }}>
                    <div style={{ fontSize: 11, color: C.textDim, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>{k.label}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono }}>{k.value}</div>
                    <div style={{ fontSize: 11, color: k.trend === "up" ? C.green : k.trend === "down" ? C.green : C.textDim, marginTop: 2 }}>{k.sub}</div>
                  </div>
                ))}
              </div>

              {/* Monthly Summary — read-only */}
              <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, padding: 20, marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Monthly Summary</div>
                <div style={{ fontSize: 13, color: C.textSec, lineHeight: 1.7 }}>
                  Your campaigns performed strongly this month. Conversions increased 12.4% vs last month while maintaining a healthy ROAS of 5.2x. We applied 4 improvements including adding negative keywords to reduce wasted spend and increasing bids on your top-converting terms. 2 new issues were identified and are being addressed by your account manager.
                </div>
              </div>

              {/* Improvements Applied — read-only view */}
              <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, padding: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Improvements Applied This Month</div>
                {[
                  { text: "Added negative keyword 'free workout plans' — saving an estimated £140/quarter", date: "Feb 18" },
                  { text: "Increased bid on 'personal trainer near me' — estimated +28 conversions/month", date: "Feb 14" },
                  { text: "Paused underperforming ad variant — improved CTR by 0.4%", date: "Feb 10" },
                  { text: "Reallocated £1,200/mo from Display to Search — ROAS improvement expected", date: "Feb 6" },
                ].map((imp, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: i < 3 ? `1px solid ${C.border}` : "none", fontSize: 13, color: C.textSec }}>
                    <span style={{ color: C.green, fontSize: 16 }}>✓</span>
                    <span style={{ flex: 1 }}>{imp.text}</span>
                    <span style={{ fontSize: 11, color: C.textDim }}>{imp.date}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {section === "performance" && (
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Campaign Performance</div>
              <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>
                    {["Campaign", "Status", "Spend", "Clicks", "Conv.", "CPA", "ROAS"].map(h => (
                      <th key={h} style={{ textAlign: "left", padding: "12px 14px", color: C.textDim, fontWeight: 600, fontSize: 10, textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {[
                      { name: "Brand — Exact", status: "Active", spend: "£1,240", clicks: 820, conv: 98, cpa: "£12.65", roas: "8.4x" },
                      { name: "Non-Brand — Search", status: "Active", spend: "£4,800", clicks: 1640, conv: 124, cpa: "£38.71", roas: "4.2x" },
                      { name: "Competitor Terms", status: "Active", spend: "£2,100", clicks: 580, conv: 42, cpa: "£50.00", roas: "3.1x" },
                      { name: "Shopping — Products", status: "Active", spend: "£3,200", clicks: 2100, conv: 68, cpa: "£47.06", roas: "4.8x" },
                      { name: "Retargeting — Display", status: "Paused", spend: "£1,110", clicks: 420, conv: 10, cpa: "£111.00", roas: "1.2x" },
                    ].map((c, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${C.border}08` }}>
                        <td style={{ padding: "10px 14px", fontWeight: 500 }}>{c.name}</td>
                        <td style={{ padding: "10px 14px" }}><span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: c.status === "Active" ? `${C.green}18` : `${C.textDim}18`, color: c.status === "Active" ? C.green : C.textDim, fontWeight: 600 }}>{c.status}</span></td>
                        <td style={{ padding: "10px 14px", fontFamily: mono }}>{c.spend}</td>
                        <td style={{ padding: "10px 14px", fontFamily: mono }}>{c.clicks.toLocaleString()}</td>
                        <td style={{ padding: "10px 14px", fontFamily: mono }}>{c.conv}</td>
                        <td style={{ padding: "10px 14px", fontFamily: mono }}>{c.cpa}</td>
                        <td style={{ padding: "10px 14px", fontFamily: mono, color: parseFloat(c.roas) >= 4 ? C.green : C.amber }}>{c.roas}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: 11, color: C.textDim, marginTop: 8, textAlign: "center" }}>Read-only view · Contact your account manager for changes</div>
            </div>
          )}

          {["segments", "budgets", "scorecard", "reports"].includes(section) && (
            <div style={{ textAlign: "center", padding: 60 }}>
              <div style={{ fontSize: 28, marginBottom: 12 }}>📊</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{sectionLabels[section]}</div>
              <div style={{ fontSize: 13, color: C.textDim, maxWidth: 400, margin: "0 auto" }}>
                This section displays your {section} data filtered to your account only. In production, this renders the same PPC Pulse {section} view but with all action buttons removed and data scoped to the client's assigned account(s).
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// MAIN APP — Agency Hub Shell
// ═══════════════════════════════════════════
export default function AgencyHub() {
  const [user, setUser] = useState(null);
  const [activeModule, setActiveModule] = useState(null);

  if (!user) {
    return <AuthGate onAuth={setUser}>{null}</AuthGate>;
  }

  const role = user.role || "viewer";
  const roleCfg = ROLE_CONFIG[role] || ROLE_CONFIG.viewer;
  const isClient = role === "viewer";
  const allowedModules = roleCfg.modules;

  // ── Client gets a dedicated clean dashboard ──
  if (isClient) {
    return <ClientDashboard user={user} />;
  }

  // ── Launcher (admin + manager) ──
  if (!activeModule) {
    const allModules = [
      { id: "ppc", label: "PPC Pulse", desc: "Google Ads optimization, AI improvements, budget pacing, error detection, and account scorecards", icon: "⚡", color: C.accent, stats: "10 sections · 6 accounts" },
      { id: "feedforge", label: "FeedForge", desc: "Shopping feed management, product optimization, feed rules, quality audits, and XML export", icon: "🛒", color: C.cyan, stats: "5 sections · WooCommerce sync" },
      { id: "campaignai", label: "Campaign Builder", desc: "AI-powered campaign generation. Enter a client brief and get full campaign structures with keywords, ad copy, and budget allocation", icon: "🤖", color: C.pink, stats: "AI-powered · Instant builds" },
      { id: "keywordai", label: "Keyword Research", desc: "AI keyword research with clusters, search volumes, CPC estimates, long-tail opportunities, and question keywords", icon: "🔬", color: C.green, stats: "AI-powered · Deep research" },
      { id: "pro", label: "Pro Tools", desc: "Auto rules, change history, client portal, anomaly detection, competitors, landing page analysis, A/B testing, forecasting, and more", icon: "🚀", color: C.pink, stats: "14 advanced tools" },
      { id: "activity", label: "Activity Feed", desc: "Unified audit log of all actions taken across both modules with impact tracking", icon: "📋", color: C.purple, stats: "Real-time timeline" },
      { id: "settings", label: "Settings", desc: "Team management, API connections, notifications, and agency preferences", icon: "⚙", color: C.textSec, stats: "Admin panel" },
    ];
    const modules = allModules.filter(m => allowedModules.includes(m.id));

    return (
      <div style={s.page}>
        <div style={{ position: "fixed", inset: 0, backgroundImage: `linear-gradient(${C.border}20 1px, transparent 1px), linear-gradient(90deg, ${C.border}20 1px, transparent 1px)`, backgroundSize: "40px 40px", zIndex: 0 }} />
        <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 40 }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div style={{ fontSize: 42, fontWeight: 800, background: `linear-gradient(135deg, ${C.accent}, ${C.cyan}, ${C.purple})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", marginBottom: 8 }}>Agency Hub</div>
            <div style={{ fontSize: 15, color: C.textSec, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>Welcome back, {user.name}. <RoleBadge role={role} /></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, maxWidth: 960, width: "100%" }}>
            {modules.map(m => (
              <div key={m.id} onClick={() => setActiveModule(m.id)} style={{ ...s.card, padding: 24, cursor: "pointer", transition: "all 0.2s", border: `1px solid ${C.border}` }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = m.color; e.currentTarget.style.transform = "translateY(-4px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.transform = "none"; }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>{m.icon}</div>
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>{m.label}</div>
                <div style={{ fontSize: 13, color: C.textSec, lineHeight: 1.5, marginBottom: 16 }}>{m.desc}</div>
                <div style={{ fontSize: 11, color: m.color, fontWeight: 600 }}>{m.stats}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 32, fontSize: 12, color: C.textDim, display: "flex", alignItems: "center", gap: 8 }}>
            Signed in as {user.email} · <RoleBadge role={role} /> · <span style={{ cursor: "pointer", color: C.accent }} onClick={() => setUser(null)}>Sign out</span>
          </div>
        </div>
      </div>
    );
  }

  // ── Guard: redirect if accessing a module they shouldn't ──
  if (!allowedModules.includes(activeModule)) {
    return (
      <div style={{ ...s.page, display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: 48 }}>🔒</div>
        <div style={{ fontSize: 18, fontWeight: 700 }}>Access Denied</div>
        <div style={{ fontSize: 13, color: C.textDim }}>Your role ({roleCfg.label}) does not have access to this module.</div>
        <button style={s.btn} onClick={() => setActiveModule(null)}>← Back to Hub</button>
      </div>
    );
  }

  // ── Module View with Top Bar ──
  const navItems = allowedModules;
  const navLabels = { ppc: "⚡ PPC", feedforge: "🛒 Feed", campaignai: "🤖 Campaigns", keywordai: "🔬 Keywords", pro: "🚀 Pro", activity: "📋 Activity", settings: "⚙ Settings" };

  const topBar = (
    <div style={{ height: 48, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", padding: "0 16px", gap: 12, background: C.surface, flexShrink: 0 }}>
      <button style={{ ...s.btnGhost, padding: "4px 12px", fontSize: 12 }} onClick={() => setActiveModule(null)}>← Hub</button>
      <div style={{ height: 20, width: 1, background: C.border }} />
      {navItems.map(m => (
        <button key={m} style={{ background: activeModule === m ? `${C.accent}20` : "transparent", color: activeModule === m ? C.accent : C.textDim, border: "none", borderRadius: 6, padding: "4px 12px", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: font }}
          onClick={() => setActiveModule(m)}>
          {navLabels[m]}
        </button>
      ))}
      <div style={{ marginLeft: "auto", fontSize: 12, color: C.textDim, display: "flex", alignItems: "center", gap: 8 }}>{user.name} <RoleBadge role={role} /> <span style={{ cursor: "pointer", color: C.accent }} onClick={() => setUser(null)}>Sign out</span></div>
    </div>
  );

  return (
    <div style={{ ...s.page, display: "flex", flexDirection: "column", height: "100vh" }}>
      {topBar}
      <div style={{ flex: 1, overflow: "hidden" }}>
        {activeModule === "ppc" && <PPCPulse />}
        {activeModule === "feedforge" && <FeedForge />}
        {activeModule === "campaignai" && <AICampaignBuilder />}
        {activeModule === "keywordai" && <AIKeywordResearch />}
        {activeModule === "pro" && <ProTools />}
        {activeModule === "activity" && <ActivityFeed />}
        {activeModule === "settings" && <SettingsPanel user={user} />}
      </div>
    </div>
  );
}
